import { useEffect, useMemo, useRef, useState } from "react";
import { Drawer, Modal, Select, message } from "antd";
import { Link, useNavigate, useParams } from "react-router-dom";
import GooglePlacesAutocompleteInput from "../../common/GooglePlacesAutocompleteInput";
import { CareShell, Panel } from "../../components/care/CareLayout";
import CareRichTextEditor from "../../components/care/CareRichTextEditor";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import useAppConfig from "../../hooks/access-control/useAppConfig";
import { careService } from "../../services/careService";
import { publicAsset } from "./careData";

const residentSections = [
  ["carePlan", "ti-heart-handshake", "Negotiated Care Plans", "Preliminary plans, 30-day observations, final NCPs, and state confirmations.", "In review", "2024-05-24T08:30:00"],
  ["documents", "ti-file-description", "Documents", "Signed agreements, policies, PoA documents, and state files.", "8 files", "2024-05-23T16:10:00"],
  ["alerts", "ti-alert-triangle", "Incidents & Alerts", "Open incidents, care risks, and follow-up items.", "2 open", "2024-05-23T11:20:00"],
  ["assessments", "ti-clipboard-text", "Assessments", "State assessments, annual reviews, and assessment changes.", "2 pending", "2024-05-22T14:00:00"],
  ["team", "ti-users-group", "Care Teams", "PCP, therapy, PoA/family, social worker, and internal staff.", "5 members", "2024-05-21T10:00:00"],
].sort((a, b) => new Date(b[5]).getTime() - new Date(a[5]).getTime());

const residentTabs = [
  ["overview", "Overview"],
  ["onboarding-bio", "Bio Data"],
  ["residency-details", "Residency Details"],
  ["onboarding-contacts", "Contacts"],
  ["onboarding-care-history", "Care History"],
  ["onboarding-care-snapshot", "Care Snapshot"],
  ["assessment", "Assessments"],
  ["daily-notes", "Daily Notes"],
  ["documents", "Documents"],
  ["care-team", "Care Teams"],
  ["resident-clock", "Resident CheckInOut"],
];

const dailyNoteCategories = [
  "Behavior",
  "Medication",
  "Health monitoring",
  "Mobility",
  "ADLs and personal care",
  "Toileting and continence",
  "Meals and hydration",
  "Safety and supervision",
  "Communication and sensory",
  "Activities and engagement",
  "Resident preferences",
  "Family update",
  "Care discrepancy",
  "Other",
];

const dailyNoteFrequencyOptions = ["N/A", "1 time/day", "2 times/day", "3 times/day", "More than 3 times/day", "1 time/week", "2 times/week", "3 times/week", "More than 3 times/week"];
const dailyNoteMagnitudeOptions = ["N/A", "None", "Mild", "Moderate", "Significant", "Severe"];
const dailyNoteTrendOptions = ["N/A", "Improved", "Worsened", "No change", "New", "Resolved"];
const residentClockReasonOptions = [
  "Hospital visit",
  "Medical appointment",
  "Dental appointment",
  "Walk",
  "Family visit",
  "Community outing",
  "Adult day program",
  "Church or spiritual activity",
  "Shopping",
  "Emergency",
  "Other",
];

const cbhsBehaviorSelectionItems = [
  "Multiple assaults related to a behavioral-health condition",
  "Physical aggression",
  "Hitting, kicking, punching or biting",
  "Pushing, grabbing or scratching",
  "Throwing objects",
  "Attempts or threats to injure others",
  "Self-endangering behavior likely to cause bodily harm",
  "Self-harm or suicidal behavior",
  "Head banging, cutting or striking self",
  "Unsafe elopement or entering traffic",
  "Ingesting unsafe substances",
  "Removing medically necessary equipment",
  "Intrusiveness that places the person at risk of assault by others",
  "Rummaging",
  "Entering other residents' rooms",
  "Taking belongings",
  "Unawareness of personal boundaries",
  "Following, touching or provoking others",
  "Chronic psychiatric symptoms requiring monitoring or redirection",
  "Hallucinations, delusions or paranoia",
  "Psychosis or responding to internal stimuli",
  "Severe agitation, mania or anxiety",
  "Disorganized thinking or emotional dysregulation",
  "Symptoms escalating toward crisis",
  "Sexual inappropriateness compromising safety",
  "Unwanted sexual touching",
  "Sexual aggression or intrusive sexual conduct",
  "Public exposure or masturbation",
  "Sexual boundary violations",
  "Entering another resident's room for sexual purposes",
];

const dailyNoteQuickSelectionsByCategory = {
  Behavior: [
    "Calm/cooperative",
    "Confused",
    "Agitated",
    "Forgetful",
    "Wandering",
    "Refused care",
    "Responded well to redirection",
    ...cbhsBehaviorSelectionItems,
  ],
  Medication: ["Medication taken", "Medication refused", "Taken with prompting", "Side effects observed", "PCP notified", "No medication concerns"],
  "Health monitoring": ["Pain reported", "No pain reported", "Vitals checked", "Weight monitored", "New health concern observed"],
  Mobility: ["Ambulated independently", "Ambulated with 1 assist", "Used walker", "Used wheelchair", "Transfer with 1 assist", "No mobility change"],
  "ADLs and personal care": ["Dressing assisted", "Bathing assisted", "Grooming completed", "Skin check completed", "Refused personal care", "No ADL concerns"],
  "Toileting and continence": ["Incontinent episode", "Toileting assistance provided", "Bowel movement recorded", "No toileting concerns"],
  "Meals and hydration": ["Ate well", "Poor appetite", "Fluids encouraged", "Refused meal", "No nutrition concerns"],
  "Safety and supervision": ["Fall risk observed", "Needed close supervision", "Room/environment checked", "No safety concerns observed"],
  "Communication and sensory": ["Communicated clearly", "Confused conversation", "Hearing difficulty observed", "No communication concerns"],
  "Activities and engagement": ["Participated in activity", "Enjoyed music", "Took walk", "Declined group activity"],
  "Resident preferences": ["Routine preference noted", "New preference discovered", "Preferred family contact"],
  "Family update": ["Family visit", "Family call", "PoA/guardian update", "Follow-up needed"],
  "Care discrepancy": ["Change from assessment observed", "New care need identified", "Add to preliminary plan", "Add to negotiated care plan"],
  Other: [],
};

const DAILY_NOTE_VISIBLE_SELECTION_COUNT = 12;

const dailyNoteCategorySearchTags = {
  Behavior: ["cbhs", "mental health", "psych", "psychiatric", "mood", "risk", "aggression", "behavioral"],
  Medication: ["emar", "mar", "meds", "rx", "drug", "pharmacy", "refusal"],
  "Health monitoring": ["vitals", "blood pressure", "bp", "pulse", "temperature", "blood sugar", "glucose", "pain", "weight"],
  Mobility: ["walking", "walk", "transfer", "ambulation", "gait", "wheelchair", "walker", "fall"],
  "ADLs and personal care": ["activities of daily living", "personal hygiene", "bathing", "dressing", "grooming", "skin"],
  "Toileting and continence": ["bathroom", "bowel", "bladder", "incontinence", "continence", "commode", "bm"],
  "Meals and hydration": ["food", "nutrition", "eating", "drink", "fluids", "swallowing", "appetite"],
  "Safety and supervision": ["supervision", "monitoring", "risk", "fall", "alarm", "environment", "evacuation"],
  "Communication and sensory": ["speech", "hearing", "vision", "glasses", "cues", "language"],
  "Activities and engagement": ["activity", "music", "walk", "social", "group", "engagement"],
  "Resident preferences": ["preference", "routine", "likes", "choice", "favorite"],
  "Family update": ["family", "poa", "guardian", "representative", "call", "visit"],
  "Care discrepancy": ["change", "difference", "assessment", "mag", "care plan", "ncp", "new need"],
};

const dailyNoteSelectionSearchTags = {
  "Calm/cooperative": ["stable", "settled", "pleasant", "cooperative", "baseline"],
  Confused: ["disoriented", "not oriented", "forgetful", "cognitive", "memory"],
  Agitated: ["restless", "upset", "irritable", "angry", "escalated"],
  Forgetful: ["memory", "short-term", "repeats", "cognitive"],
  "Physically aggressive": ["assault", "violent", "combative", "hit", "kick", "punch", "bite"],
  Wandering: ["pacing", "roaming", "walkabout", "exit seeking", "elopement"],
  "Exit-seeking/elopement concern": ["elope", "left home", "door", "outside", "traffic", "unsafe exit"],
  "Refused care": ["refusal", "declined", "resistive", "noncompliance", "would not"],
  "Responded well to redirection": ["redirect", "cue", "de-escalate", "calmed", "reassurance"],
  "Multiple assaults related to a behavioral-health condition": ["assaultive", "attack", "violent", "physical aggression", "harm others"],
  "Physical aggression": ["assault", "violent", "combative", "hit", "kick", "punch", "bite"],
  "Hitting, kicking, punching or biting": ["hit", "kick", "punch", "bite", "strike", "assault"],
  "Pushing, grabbing or scratching": ["push", "grab", "scratch", "shove", "pull"],
  "Throwing objects": ["throw", "threw", "items", "objects", "property"],
  "Attempts or threats to injure others": ["threat", "harm others", "injure", "attack", "assault"],
  "Self-endangering behavior likely to cause bodily harm": ["self danger", "unsafe", "harm self", "danger to self"],
  "Self-harm or suicidal behavior": ["suicide", "suicidal", "self injury", "self injurious", "harm self"],
  "Head banging, cutting or striking self": ["headbang", "cutting", "strike self", "hitting self", "self injury"],
  "Unsafe elopement or entering traffic": ["elope", "exit", "run away", "traffic", "road", "street"],
  "Ingesting unsafe substances": ["eat unsafe", "swallow", "poison", "chemicals", "pica"],
  "Removing medically necessary equipment": ["pulling tube", "oxygen", "iv", "catheter", "medical device"],
  "Intrusiveness that places the person at risk of assault by others": ["intrusive", "provoking", "boundaries", "peer conflict"],
  Rummaging: ["searching", "digging", "going through belongings"],
  "Entering other residents' rooms": ["wrong room", "room intrusion", "resident room", "bedroom"],
  "Taking belongings": ["stealing", "property", "items", "possessions"],
  "Unawareness of personal boundaries": ["boundaries", "too close", "personal space", "intrusive"],
  "Following, touching or provoking others": ["follow", "touch", "provoke", "harass", "peer"],
  "Chronic psychiatric symptoms requiring monitoring or redirection": ["mental health", "psych", "psychiatric", "monitoring"],
  "Hallucinations, delusions or paranoia": ["hallucinating", "seeing things", "hearing voices", "delusional", "paranoid"],
  "Psychosis or responding to internal stimuli": ["psychotic", "internal stimuli", "voices", "talking to self"],
  "Severe agitation, mania or anxiety": ["manic", "anxious", "panic", "restless", "escalation"],
  "Disorganized thinking or emotional dysregulation": ["disorganized", "dysregulated", "labile", "confused thoughts"],
  "Symptoms escalating toward crisis": ["crisis", "escalation", "decompensation", "unsafe"],
  "Sexual inappropriateness compromising safety": ["sexual behavior", "inappropriate", "boundary", "safety"],
  "Unwanted sexual touching": ["sexual touch", "groping", "unwanted contact"],
  "Sexual aggression or intrusive sexual conduct": ["sexual aggression", "sexual intrusion", "assaultive sexual"],
  "Public exposure or masturbation": ["disrobing", "exposure", "masturbation", "undressing"],
  "Sexual boundary violations": ["sexual boundary", "inappropriate sexual", "privacy"],
  "Entering another resident's room for sexual purposes": ["sexual room intrusion", "bedroom", "resident room"],
};

const dailyNoteSearchText = (...values) =>
  values.flatMap((value) => (Array.isArray(value) ? value : [value])).filter(Boolean).join(" ").toLowerCase();

const dailyNoteSelectionGroups = (groups, category, query, showAll) => {
  const text = String(query || "").trim().toLowerCase();
  const groupRows = Object.entries(groups).map(([groupCategory, selections]) => {
    const categoryMatches = text && dailyNoteSearchText(groupCategory, dailyNoteCategorySearchTags[groupCategory]).includes(text);
    const matchingSelections = (selections || [])
      .filter((selection) => !text || categoryMatches || dailyNoteSearchText(selection, dailyNoteSelectionSearchTags[selection]).includes(text))
      .map((selection) => ({
        category: groupCategory,
        label: selection,
        path: `${groupCategory} > ${selection}`,
      }));
    return {
      category: groupCategory,
      selections: text ? matchingSelections : matchingSelections.filter((selection) => selection.category === category),
    };
  }).filter((group) => group.selections.length);

  if (text || showAll) return groupRows;

  let remaining = DAILY_NOTE_VISIBLE_SELECTION_COUNT;
  return groupRows.map((group) => {
    const selections = group.selections.slice(0, remaining);
    remaining -= selections.length;
    return { ...group, selections };
  }).filter((group) => group.selections.length);
};

const dailyNoteSelectionCount = (groups, category, query) => {
  const text = String(query || "").trim().toLowerCase();
  return dailyNoteSelectionGroups(groups, category, text, true)
    .reduce((total, group) => total + group.selections.length, 0);
};

const carePlanFlowStages = [
  {
    label: "State Initial Assessment",
    existingLabel: "Yearly State Assessment",
    text: "State assessment received",
    existingText: "Annual state assessment received",
    stageKeys: ["STATE_INITIAL_ASSESSMENT", "YEARLY_STATE_ASSESSMENT", "STATE_ASSESSMENT_RECEIVED"],
    icon: "ti-flag",
    appliesTo: ["new-resident", "existing-resident"],
  },
  {
    label: "MaG / Review",
    text: "Meet-and-Greet and provider decision",
    stageKeys: ["MEET_AND_GREET", "PROVIDER_REVIEW", "PROVIDER_ACCEPTED"],
    icon: "ti-clipboard-text",
    appliesTo: ["new-resident"],
  },
  {
    label: "Annual Review",
    text: "Review yearly notes and changes",
    stageKeys: ["ANNUAL_REVIEW", "YEARLY_REVIEW", "ANNUAL_ASSESSMENT"],
    icon: "ti-notes",
    appliesTo: ["existing-resident"],
  },
  {
    label: "Onboarding",
    text: "Signed paperwork and admission details",
    stageKeys: ["ADMISSION_AND_PAPERWORK", "CLIENT_ONBOARDING"],
    icon: "ti-clipboard-list",
    appliesTo: ["new-resident"],
  },
  {
    label: "30 Days Probation",
    text: "Observation period complete",
    stageKeys: ["THIRTY_DAY_PROBATION", "THIRTY_DAY_OBSERVATION", "NEW_RESIDENT_ASSESSMENT"],
    icon: "ti-notes",
    appliesTo: ["new-resident"],
  },
  {
    label: "Preliminary Plan",
    text: "Provider plan after 30-day notes",
    stageKeys: ["PRELIMINARY_CARE_PLAN"],
    icon: "ti-heart-handshake",
    appliesTo: ["new-resident"],
  },
  {
    label: "NCP",
    text: "Template, provider inputs, and pay negotiation",
    stageKeys: ["NEGOTIATED_CARE_PLAN", "NEGOTIATED_CARE_PLAN_DRAFT", "NCP_IN_PROGRESS", "PAYMENT_NEGOTIATION"],
    icon: "ti-file-pencil",
    appliesTo: ["new-resident", "existing-resident"],
  },
  {
    label: "Confirmed / Ongoing",
    text: "State confirmed files and ongoing daily notes",
    stageKeys: ["FINAL_ASSESSMENT_UPDATE", "STATE_CONFIRMATION", "ONGOING_CARE", "ACTIVE_CARE", "CONFIRMED", "CURRENT"],
    icon: "ti-circle-check",
    appliesTo: ["new-resident", "existing-resident"],
  },
];

const initialsFor = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "--";
const compactDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
const ageFromBirthDate = (birthDate) => {
  if (!birthDate) return "";
  const date = new Date(birthDate);
  if (Number.isNaN(date.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const hasBirthdayPassed =
    today.getMonth() > date.getMonth() ||
    (today.getMonth() === date.getMonth() && today.getDate() >= date.getDate());
  if (!hasBirthdayPassed) age -= 1;
  return age > -1 ? String(age) : "";
};
const fullNameFor = (resident = {}) =>
  resident.name ||
  resident.fullName ||
  resident.residentName ||
  [resident.firstName, resident.middleName, resident.lastName].filter(Boolean).join(" ") ||
  "Resident";
const locationFor = (resident = {}) => resident.locationInformation || resident.address || resident.location || {};
const contactFor = (resident = {}) => resident.contactInformation || resident.contact || {};
const addressSummaryFor = (resident = {}) => {
  const location = locationFor(resident);
  return [
    location.addressLine1 || location.address,
    location.addressLine2,
    location.city,
    location.county,
    location.state,
    location.country,
    location.zipCode || location.zip,
  ].filter(Boolean).join(", ");
};
const primaryPhoneFor = (resident = {}) => {
  const contact = contactFor(resident);
  return resident.primaryContactPhone || contact.primaryPhone || resident.phone || "";
};
const primaryEmailFor = (resident = {}) => {
  const contact = contactFor(resident);
  return resident.primaryContactEmail || contact.primaryEmail || resident.email || "";
};
const displayValue = (value, fallback = "Not set") => {
  if (Array.isArray(value)) return value.filter(Boolean).join(", ") || fallback;
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
};
const labelize = (value) => {
  const text = displayValue(value, "");
  return text ? text.replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase()) : "";
};
const normalizeStatusKey = (value) => String(value || "").trim().replace(/[\s-]+/g, "_").toUpperCase();
const hasHtml = (value) => /<\/?[a-z][\s\S]*>/i.test(String(value || ""));
const onboardingValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.filter(Boolean).map(onboardingValue).join(", ");
  if (typeof value === "object") return Object.entries(value)
    .filter(([, entryValue]) => entryValue !== null && entryValue !== undefined && entryValue !== "")
    .map(([key, entryValue]) => `${labelize(key)}: ${onboardingValue(entryValue)}`)
    .join(" | ");
  return String(value);
};
const onboardingDocumentUrlFor = (document = {}) =>
  document.signedUrl ||
  document.downloadUrl ||
  document.previewUrl ||
  document.url ||
  document.fileUrl ||
  "";
const isImageOnboardingDocument = (document = {}) => {
  const contentType = String(document.contentType || document.mimeType || document.type || "").toLowerCase();
  const fileName = String(document.fileName || document.name || "").toLowerCase();
  return contentType.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(fileName);
};
const onboardingDocumentMetaFor = (document = {}) =>
  [
    document.description,
    document.fileName,
    document.addedAt ? `Added ${compactDate(document.addedAt)}` : "",
    document.confirmed ? "Confirmed" : "",
    document.storageStatus,
  ].filter(Boolean).join(" | ");
const addressSummary = (address = {}) => [
  address.addressLine1 || address.address,
  address.addressLine2,
  address.city,
  address.county,
  address.state,
  address.country,
  address.zipCode || address.zip,
].filter(Boolean).join(", ");
const phonesSummary = (phones = {}) => [
  phones.preferred ? `Preferred: ${phones.preferred}` : "",
  phones.mobile ? `Mobile: ${phones.mobile}` : "",
  phones.office ? `Office: ${phones.office}` : "",
  phones.home ? `Home: ${phones.home}` : "",
].filter(Boolean).join(" | ");
const processDateValue = (process = {}) => {
  const value = process.updatedAt || process.createdAt || process.startDate || process.processStartDate || "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};
const assessmentWorkflowStatus = (assessment = {}) =>
  firstText(assessment.workflowStatus, assessment.processStatus, assessment.status);
const closedAssessmentStatuses = ["COMPLETED", "COMPLETE", "CONFIRMED", "CLOSED", "CANCELLED", "CANCELED", "ARCHIVED", "DELETED"];
const isClosedAssessmentProcess = (process = {}) => {
  const status = normalizeStatusKey(assessmentWorkflowStatus(process));
  return closedAssessmentStatuses.includes(status);
};
const completedResidentStatuses = new Set(["ACTIVE", "ONBOARDED", "SUBMITTED", "COMPLETED", "COMPLETE", "CONFIRMED", "CURRENT"]);
const incompleteResidentStatuses = new Set(["PROSPECTIVE", "IN_PROGRESS", "PENDING", "DRAFT", "NEW"]);
const residentHasCompletedOnboarding = (resident = {}, preview = null) => {
  const residentStatus = normalizeStatusKey(resident.status || resident.onboardingStatus);
  const previewStatus = normalizeStatusKey(
    preview?.onboardingStepStatus?.status ||
    preview?.onboardingStatus ||
    preview?.status ||
    preview?.resident?.status,
  );
  if (completedResidentStatuses.has(residentStatus) || completedResidentStatuses.has(previewStatus)) return true;
  if (incompleteResidentStatuses.has(residentStatus) || incompleteResidentStatuses.has(previewStatus)) return false;
  return false;
};
const pickActiveAssessmentProcess = (processes = []) => {
  const list = Array.isArray(processes) ? processes : [];
  return [...list]
    .filter((process) => process && !isClosedAssessmentProcess(process))
    .sort((a, b) => processDateValue(b) - processDateValue(a))[0] || null;
};
const assessmentRedirectIdFor = ({ processes = [], assessments = [], preview = null, resident = {} }) => {
  const activeProcess = pickActiveAssessmentProcess(processes);
  const linkedAssessment = (assessments || []).find((assessment) =>
    assessment?.assessmentProcessId || assessment?.processId
  );
  return (
    activeProcess?.id ||
    activeProcess?.assessmentProcessId ||
    activeProcess?.processId ||
    preview?.assessmentProcessId ||
    preview?.previewPayload?.assessmentProcessId ||
    resident?.activeAssessmentProcessId ||
    resident?.currentAssessmentProcessId ||
    resident?.openAssessmentProcessId ||
    linkedAssessment?.assessmentProcessId ||
    linkedAssessment?.processId ||
    ""
  );
};
const stageIndexFor = (stages = [], stageKey = "") => {
  const normalized = normalizeStatusKey(stageKey);
  return stages.findIndex((stage) => (stage.stageKeys || []).map(normalizeStatusKey).includes(normalized));
};
const residentAssessmentPatientType = (assessment = {}) => {
  const raw = normalizeStatusKey(assessment.patientType || assessment.residentType || assessment.type);
  if (raw.includes("NEW")) return "NEW";
  if (raw.includes("ANNUAL") || raw.includes("RESIDENT")) return "RESIDENT";
  return raw || "Not set";
};
const residentAssessmentTitle = (assessment = {}) =>
  assessment.name || assessment.title || labelize(assessment.type || assessment.assessmentType || "Resident Assessment");
const residentAssessmentSocialWorker = (assessment = {}) =>
  assessment.socialWorkerName ||
  [assessment.socialWorkerFirstName, assessment.socialWorkerLastName].filter(Boolean).join(" ") ||
  assessment.socialWorker?.name ||
  "Not assigned";
const residentAssessmentResidentId = (assessment = {}) =>
  assessment.residentId ||
  assessment.clientId ||
  assessment.resident?.id ||
  assessment.resident?.residentId ||
  assessment.residentData?.id ||
  assessment.residentData?.residentId ||
  assessment.residentDetails?.id ||
  assessment.residentDetails?.residentId ||
  assessment.stageReferences?.residentId ||
  assessment.references?.residentId ||
  "";
const residentAssessmentResidentName = (assessment = {}) =>
  assessment.residentName ||
  assessment.residentFullName ||
  assessment.clientName ||
  assessment.resident?.name ||
  assessment.resident?.fullName ||
  [assessment.resident?.firstName, assessment.resident?.middleName, assessment.resident?.lastName].filter(Boolean).join(" ") ||
  "";
const residentAssessmentRecordItems = (assessment = {}) => [
  residentAssessmentTitle(assessment),
  assessment.uploadedDocuments ? "Uploaded documents" : "",
  assessment.signedDocuments ? "Signed documents" : "",
  assessment.meetingDate ? "Meeting details" : "",
  assessment.summary ? "Assessment summary" : "",
].filter(Boolean);
const linkedAssessmentProcessIdFor = (assessment = {}) =>
  assessment.assessmentProcessId || assessment.processId || "";
const assessmentProcessIdFor = (assessment = {}) =>
  linkedAssessmentProcessIdFor(assessment) || assessment.id || assessment.assessmentId || "";
const assessmentProcessRouteIdFor = (assessment = {}) =>
  linkedAssessmentProcessIdFor(assessment) || (assessment.__assessmentProcessSource ? assessment.id : "");
const residentAssessmentDedupeKey = (assessment = {}) => {
  const assessmentProcessId = linkedAssessmentProcessIdFor(assessment);
  if (assessmentProcessId) return `assessment-process:${assessmentProcessId}`;
  return [
    "resident-assessment",
    residentAssessmentResidentId(assessment) || residentAssessmentResidentName(assessment),
    residentAssessmentPatientType(assessment),
    assessment.processStartDate || assessment.assessmentDate || assessment.startDate || "",
    residentAssessmentTitle(assessment),
  ].map((value) => String(value || "").trim().toLowerCase()).join("|");
};
const dedupeResidentAssessments = (items = []) => {
  const byKey = new Map();
  (Array.isArray(items) ? items : []).forEach((assessment) => {
    const key = residentAssessmentDedupeKey(assessment);
    const existing = byKey.get(key);
    if (!existing || residentAssessmentDateValue(assessment) >= residentAssessmentDateValue(existing)) {
      byKey.set(key, assessment);
    }
  });
  return Array.from(byKey.values());
};
const assessmentProcessTitle = (assessment = {}) =>
  assessment.name ||
  assessment.title ||
  assessment.processName ||
  assessment.assessmentName ||
  labelize(assessment.type || assessment.assessmentType || "Resident Assessment");
const assessmentProcessRecords = (assessment = {}) => {
  const records = assessment.attachedRecords || assessment.records || assessment.assessmentRecords || [];
  if (Array.isArray(records)) {
    return records.map((record) => (typeof record === "string" ? record : record?.name || record?.title || record?.recordName || record?.recordType)).filter(Boolean);
  }
  if (typeof records === "string" && records.trim()) {
    return records.split(",").map((record) => record.trim()).filter(Boolean);
  }
  return residentAssessmentRecordItems(assessment);
};
const isClosedResidentAssessment = (assessment = {}) =>
  closedAssessmentStatuses.includes(normalizeStatusKey(assessmentWorkflowStatus(assessment)));
const isActiveResidentAssessment = (assessment = {}) => {
  const status = normalizeStatusKey(assessment.rawWorkflowStatus || assessmentWorkflowStatus(assessment));
  if (status) return !closedAssessmentStatuses.includes(status);
  return Boolean(assessment.__assessmentProcessSource || assessmentProcessRouteIdFor(assessment));
};
const residentAssessmentDateValue = (assessment = {}) => {
  const value = assessment.updatedAt || assessment.assessmentDate || assessment.processStartDate || assessment.startDate || assessment.createdAt || "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};
const mapResidentAssessmentRecord = (assessment = {}) => ({
  ...assessment,
  rawWorkflowStatus: assessmentWorkflowStatus(assessment),
  id: assessmentProcessIdFor(assessment) || assessment.id,
  processName: assessmentProcessTitle(assessment),
  title: assessmentProcessTitle(assessment),
  summary: assessment.summary || assessment.description || assessment.careNeeded || assessment.diagnosisHealthProfile || assessment.meetAndGreetNotes || (assessment.__assessmentProcessSource ? "Assessment process from care service." : "Resident assessment record from care service."),
  patientType: residentAssessmentPatientType(assessment),
  startDate: assessment.processStartDate || assessment.startDate || assessment.assessmentDate || assessment.createdAt,
  endDate: assessment.processEndDate || assessment.endDate,
  status: labelize(assessmentWorkflowStatus(assessment) || "Draft"),
  dateAdded: assessment.assessmentDate || assessment.processStartDate || assessment.startDate || assessment.createdAt || assessment.updatedAt,
  attachedRecords: assessmentProcessRecords(assessment),
  socialWorker: residentAssessmentSocialWorker(assessment),
  addedBy: assessment.assessorName || assessment.createdByName || "System",
  routePath: assessmentProcessRouteIdFor(assessment) ? `/assessments/${assessmentProcessRouteIdFor(assessment)}/new` : "",
});
const asList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.content)) return value.content;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};
const stripHtml = (value) => String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
const firstText = (...values) => values.find((value) => value !== null && value !== undefined && value !== "") || "";
const careProfileOptionId = (profile = {}) =>
  firstText(
    profile.careProfileId,
    profile.profileId,
    profile.id,
    profile.userProfile?.id,
    profile.user?.id,
    profile.userId,
    profile.userProfileId,
    profile.email,
    profile.userProfile?.email,
    profile.user?.email,
    profile.profile?.email,
    profile.profileData?.email,
  );
const careProfileUserFor = (profile = {}) => profile.userProfile || profile.user || {};
const careProfileBaseFor = (profile = {}) => profile.profile || profile.profileData || {};
const careProfileOptionName = (profile = {}) => {
  const user = careProfileUserFor(profile);
  const base = careProfileBaseFor(profile);
  return firstText(
    user.name,
    user.fullName,
    profile.name,
    profile.fullName,
    profile.displayName,
    profile.staffName,
    profile.caregiverName,
    profile.careGiverName,
    profile.careProfileName,
    base.fullName,
    base.name,
    [base.firstName, base.middleName, base.lastName].filter(Boolean).join(" "),
    [profile.firstName, profile.middleName, profile.lastName].filter(Boolean).join(" "),
    [user.firstName, user.middleName, user.lastName].filter(Boolean).join(" "),
    profile.userName,
    user.email,
    base.email,
    profile.email,
    "Care staff",
  );
};
const careProfileOptionEmail = (profile = {}) =>
  firstText(
    profile.email,
    profile.userProfile?.email,
    profile.user?.email,
    profile.profile?.email,
    profile.profileData?.email,
    profile.careProfileEmail,
    "No email added",
  );
const compactObject = (value = {}) =>
  Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined && entry !== null && entry !== ""));
const personDisplayName = (person = {}) => {
  if (!person || typeof person !== "object") return "";
  const name = firstText(
    person.name,
    person.fullName,
    person.displayName,
    [person.firstName, person.middleName, person.lastName].filter(Boolean).join(" "),
    person.username,
  );
  const email = firstText(person.email, person.primaryEmail, person.workEmail);
  return [name, email && email !== name ? email : ""].filter(Boolean).join(" | ");
};
const recordDate = (record = {}) =>
  firstText(record.dateAdded, record.happenedAt, record.occurredAt, record.noteDate, record.createdAt, record.updatedAt, record.uploadedAt, record.assessmentDate);
const addedByFor = (record = {}) =>
  firstText(
    record.capturedByName,
    record.capturedByCaregiverName,
    record.enteredByName,
    record.recordedByName,
    record.careGiverName,
    record.caregiverName,
    personDisplayName(record.capturedByUser),
    personDisplayName(record.caregiverUser),
    personDisplayName(record.careGiverUser),
    personDisplayName(record.enteredByUser),
    personDisplayName(record.recordedByUser),
    record.addedByName,
    personDisplayName(record.addedByUser),
    record.createdByName,
    personDisplayName(record.createdByUser),
    record.uploadedByName,
    record.assessorName,
    record.staffName,
    record.capturedBy,
    record.enteredBy,
    record.recordedBy,
    record.careGiver,
    record.caregiver,
    record.addedBy,
    record.createdBy,
    "System",
  );
const mapDailyNoteRecord = (note = {}) => ({
  ...note,
  title: firstText(note.title, labelize(note.category), "Daily note"),
  summary: stripHtml(firstText(note.summary, note.notes, note.note, note.description, note.evaluationNotes)) || "No note summary added.",
  dateAdded: recordDate(note),
  addedBy: addedByFor(note),
  category: labelize(firstText(note.category, note.noteCategory, note.type, "Note")),
});
const mapDocumentRecord = (document = {}) => ({
  ...document,
  title: firstText(document.title, document.name, document.fileName, document.originalFileName, "Document"),
  summary: firstText(document.description, document.summary, document.fileName, document.storageStatus, "Resident document"),
  dateAdded: recordDate(document),
  addedBy: addedByFor(document),
  status: labelize(firstText(document.status, document.storageStatus, document.documentStatus, "Available")),
});
const mapCareTeamRecord = (member = {}) => ({
  ...member,
  title: firstText(member.name, member.caregiverName, member.careGiverName, member.staffName, member.userProfile?.name, member.user?.name, [member.firstName, member.lastName].filter(Boolean).join(" "), member.organizationName, member.email, member.caregiverEmail, "Care team member"),
  summary: firstText(member.summary, member.assignment, member.description, member.email, member.caregiverEmail, phonesSummary(member.phones), member.phone, "Care team contact"),
  dateAdded: recordDate(member),
  addedBy: addedByFor(member),
  role: labelize(firstText(member.role, member.assignmentRole, member.relationship, member.contactType, "Care Team")),
});
const mapPreliminaryPlanRecord = (record = {}) => ({
  ...record,
  title: firstText(record.title, record.name, record.recordName, "Preliminary plan"),
  summary: firstText(record.summary, record.description, record.notes, record.planSummary, "Preliminary plan record"),
  dateAdded: recordDate(record),
  addedBy: addedByFor(record),
  status: labelize(firstText(record.status, record.workflowStatus, record.recordStatus, "Draft")),
});
const isPreliminaryPlanRecord = (record = {}) => {
  const key = normalizeStatusKey(firstText(record.recordType, record.type, record.stage, record.workflowStage, record.title, record.name));
  return key.includes("PRELIMINARY");
};
const splitCurrentAndPrevious = (records = [], isClosed = null) => {
  const sorted = sortedRecordRows(records);
  const current = sorted.find((record) => !isClosed || !isClosed(record)) || null;
  return {
    current,
    previous: current ? sorted.filter((record) => record !== current) : sorted,
  };
};

const parseRecordPayloadObject = (value) => {
  if (!value) return {};
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return typeof value === "object" && !Array.isArray(value) ? value : {};
};

const recordPayloadWithDailyNotes = (record = {}) => {
  const payload = parseRecordPayloadObject(record?.payload || record);
  return {
    ...record,
    ...payload,
    dailyNotes: Array.isArray(payload.dailyNotes) ? payload.dailyNotes : Array.isArray(record?.dailyNotes) ? record.dailyNotes : [],
  };
};

const dailyNoteDedupeKey = (note = {}) =>
  note?.id || `${note?.title || ""}-${note?.date || note?.noteDate || note?.happenedAt || note?.occurredAt || ""}`;

const mergeDailyNoteSources = (primaryNotes = [], fallbackNotes = []) => {
  const merged = [];
  const seen = new Set();
  [...primaryNotes, ...fallbackNotes].forEach((note) => {
    const key = dailyNoteDedupeKey(note);
    if (!key || seen.has(key)) return;
    seen.add(key);
    merged.push(note);
  });
  return merged;
};

const assessmentProcessRecordTypeFor = (process = {}, resident = {}) => {
  const patientType = normalizeStatusKey(process.patientType || process.residentType || resident.patientType || resident.residentType);
  return patientType === "RESIDENT" ? "annual-residency-progress" : "resident-30-day-probation";
};

const assessmentProcessIdValue = (process = {}) =>
  process?.id || process?.assessmentProcessId || process?.processId || "";

const dateInputValue = (date) => new Date(date).toISOString().slice(0, 10);
const timeInputValue = (date) => new Date(date).toTimeString().slice(0, 5);

const shiftDate = (date, days) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const shiftMinutes = (date, minutes) => {
  const nextDate = new Date(date);
  nextDate.setMinutes(nextDate.getMinutes() + minutes);
  return nextDate;
};

const todayDate = dateInputValue(new Date());
const yesterdayDate = dateInputValue(shiftDate(todayDate, -1));
const olderNoteDate = dateInputValue(shiftDate(todayDate, -4));

const negotiatedPlanSources = [
  ["Preliminary Plan", "Admission preliminary plan", "May 7, 2024", "Included", "ti-heart-handshake"],
  ["State Assessment", "Initial state assessment packet", "May 6, 2024", "Included", "ti-clipboard-text"],
  ["Daily Notes", "30-day observation notes and discrepancies", "May 20, 2024", "Included", "ti-notes"],
  ["MaG", "Meet-and-Greet hospital evaluation", "May 6, 2024", "Included", "ti-user-check"],
  ["Family Inputs", "PoA and family care preferences", "May 7, 2024", "Included", "ti-users-group"],
  ["Care Team Updates", "PCP, PT, social worker, and internal staff updates", "May 24, 2024", "Pending review", "ti-stethoscope"],
];

const drawerContent = {
  ...Object.fromEntries([].map((staff) => [
    staff.key,
    {
      title: staff.name,
      intro: `${staff.role} assigned to Margaret Johnson today.`,
      sections: [
        {
          title: "Staff Details",
          rows: [
            ["Name", staff.name],
            ["Role", staff.role],
            ["Shift", staff.shift],
            ["Assignment", staff.assignment],
          ],
        },
        {
          title: "Contact Information",
          rows: [
            ["Phone", staff.phone],
            ["Email", staff.email],
          ],
        },
      ],
      contactActions: [
        ["Chat", "ti-message-circle", "#chat"],
        ["Call", "ti-phone", `tel:${staff.phone.replace(/[^+\d]/g, "")}`],
      ],
      action: "View schedule",
    },
  ])),
  persona: {
    title: "Resident Persona",
    intro: "Resident bio data, contact information, and next of kin details.",
    sections: [
      {
        title: "Bio Data",
        rows: [
          ["Full name", "Margaret Johnson"],
          ["Date of birth", "August 14, 1941"],
          ["Age", "82"],
          ["Gender", "Female"],
          ["Room", "214A"],
          ["Admission date", "May 6, 2024"],
        ],
      },
      {
        title: "Contact Information",
        rows: [
          ["Phone", "(206) 555-0184"],
          ["Email", "margaret.johnson@example.com"],
          ["Address", "4821 Cedar Lane, Seattle, WA 98112"],
          ["Preferred contact", "Phone through PoA"],
        ],
      },
      {
        title: "Next of Kin",
        rows: [
          ["Name", "Elaine Johnson"],
          ["Relationship", "Daughter / PoA"],
          ["Phone", "(206) 555-0128"],
          ["Email", "elaine.johnson@example.com"],
        ],
      },
    ],
    action: "Edit persona",
  },
  documents: {
    title: "Files and Documents",
    intro: "Review signed paperwork, state assessment files, PoA documents, and confirmed care plan files.",
    rows: [
      ["State Initial Assessment.pdf", "Uploaded May 6, 2024"],
      ["Admission Agreements.pdf", "Signed May 7, 2024"],
      ["PoA Documentation.pdf", "Verified May 7, 2024"],
      ["Negotiated Care Plan Draft.pdf", "Updated May 24, 2024"],
    ],
    action: "Upload file",
  },
  assessments: {
    title: "Assessments",
    intro: "Review state assessments, annual assessment changes, and assessment items that affect care planning.",
    rows: [
      ["Initial state assessment", "Medication behavior, diagnosis, history, and care needs imported."],
      ["MaG evaluation", "Provider review notes show transfer discrepancy."],
      ["Final assessment update", "Pending final provider input before state confirmation."],
    ],
    action: "Add new assessment",
  },
  assessmentCreate: {
    title: "New Assessment",
    intro: "Add a state assessment record, attach the source files, and capture the care planning details that should feed the resident record.",
    form: "assessment",
  },
  carePlan: {
    title: "Negotiated Care Plans",
    intro: "Manage 30-day observations, preliminary plans, provider inputs, pay negotiation, and state confirmation.",
    rows: [
      ["30-day probation", "18 notes captured; discrepancies documented."],
      ["Preliminary care plan", "Created after the 30-day notes are reviewed."],
      ["Final NCP", "Ready for provider review and social worker negotiation."],
    ],
    action: "Open NCP workspace",
  },
  team: {
    title: "Care Teams",
    intro: "Coordinate updates with PCP, therapy, social worker, PoA/family, and internal care staff.",
    rows: [
      ["PCP", "Dr. Helen Morris"],
      ["Physical Therapist", "Jason Reed"],
      ["PoA / Family", "Elaine Johnson"],
      ["Social Worker", "Amanda Parker"],
    ],
    action: "Message care team",
  },
  alerts: {
    title: "Incidents and Alerts",
    intro: "Track incidents, health or welfare risks, and follow-up items.",
    rows: [
      ["Fall risk", "High priority; hallway walk supervision required."],
      ["Transfer discrepancy", "State assessment conflicts with MaG/home observation."],
    ],
    action: "Add alert",
  },
};

const tabItems = {
  assessment: {
    createLabel: "Start New Assessment",
    drawerKey: "assessments",
    createDrawerKey: "assessmentCreate",
    currentLabel: "Current assessment process",
    gridTemplate: "minmax(180px, 1fr) 105px 120px 120px 115px minmax(190px, 1.1fr) 135px 28px",
    columns: [
      ["Assessment Process", "processName"],
      ["Patient Type", "patientType", "status"],
      ["Start Date", "startDate", "date"],
      ["End Date", "endDate", "date"],
      ["Status", "status", "status"],
      ["Attached Records", "attachedRecords", "attachedRecords"],
      ["Social Worker", "socialWorker"],
    ],
    current: null,
    rows: [],
  },
  "preliminary-plan": {
    createLabel: "Create preliminary plan",
    drawerKey: "carePlan",
    currentLabel: "Current preliminary plan",
    gridTemplate: "minmax(190px, 1fr) 130px 150px 120px 28px",
    columns: [
      ["Plan", "title"],
      ["Created", "dateAdded", "date"],
      ["Prepared By", "addedBy"],
      ["Status", "status", "status"],
    ],
    current: null,
    rows: [],
  },
  "daily-notes": {
    currentLabel: "Latest daily note",
    gridTemplate: "minmax(220px, 1fr) 130px 145px 120px 28px",
    columns: [
      ["Note", "title"],
      ["Date Added", "dateAdded", "date"],
      ["Captured By", "addedBy"],
      ["Category", "category", "status"],
    ],
    current: null,
    rows: [],
  },
  documents: {
    createLabel: "Upload document",
    drawerKey: "documents",
    currentLabel: "Current document",
    gridTemplate: "minmax(220px, 1fr) 130px 145px 120px 28px",
    columns: [
      ["File", "title"],
      ["Date Added", "dateAdded", "date"],
      ["Uploaded By", "addedBy"],
      ["Status", "status", "status"],
    ],
    current: null,
    rows: [],
  },
  "care-team": {
    createLabel: "Add care team member",
    drawerKey: "team",
    currentLabel: "Primary care team member",
    gridTemplate: "minmax(190px, 1fr) 130px 145px 140px 28px",
    columns: [
      ["Team Member", "title"],
      ["Date Added", "dateAdded", "date"],
      ["Added By", "addedBy"],
      ["Role", "role", "status"],
    ],
    current: null,
    rows: [],
  },
};

const sortedRecordRows = (rows) =>
  [...rows].sort((a, b) => {
    const aTime = new Date(a.dateAdded || 0).getTime();
    const bTime = new Date(b.dateAdded || 0).getTime();
    return (Number.isNaN(bTime) ? 0 : bTime) - (Number.isNaN(aTime) ? 0 : aTime);
  });

const recordTitle = (record) => record.period || record.title || record.name || record.file;

const recordSummary = (record) => record.summary || record.socialWorker || record.addedBy || record.status || record.role || record.category;

const recordBadge = (record) => record.status || record.role || record.category || "Current";

const formatDate = (date) => {
  const parsed = new Date(date);
  if (!date || Number.isNaN(parsed.getTime())) return "Not set";
  return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const formatDateTime = (date) => {
  const parsed = new Date(date);
  if (!date || Number.isNaN(parsed.getTime())) return "Not set";
  return parsed.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

const dateTimeLocalValue = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

const isoFromDateTimeLocal = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
};

const residentClockOutTime = (record = {}) =>
  firstText(record.clockOutTime, record.clockOutAt, record.clockOutTimestamp, record.outTime, record.leftAt, record.startTime, record.createdAt);

const residentClockInTime = (record = {}) =>
  firstText(record.clockInTime, record.clockInAt, record.clockInTimestamp, record.inTime, record.returnedAt, record.endTime);

const residentExpectedBackTime = (record = {}) =>
  firstText(record.expectedBackTime, record.expectedBackAt, record.expectedReturnTime, record.expectedReturnAt, record.returnExpectedAt);

const residentClockDestinationAddress = (record = {}) =>
  firstText(record.destinationLocationAddress, record.destinationAddress, record.destination, record.locationAddress, record.location);

const residentClockEmergencyContactPhone = (record = {}) =>
  firstText(record.emergencyContactPhone, record.emergencyPhone, record.contactPhone, record.emergencyContact?.phone);

const isResidentClockedOut = (record = {}) => {
  if (!record) return false;
  if (!residentClockOutTime(record)) return false;
  const status = String(record.status || "").toUpperCase();
  return !residentClockInTime(record) && (!status || ["CLOCKED_OUT", "OUT", "OPEN", "ACTIVE"].includes(status));
};

const noteTimeRange = (note = {}) => {
  const start = firstText(note.startTimestamp, note.rangeStartTimestamp, note.startedAt, note.date, note.happenedAt, note.occurredAt, note.noteDate);
  const end = firstText(note.endTimestamp, note.rangeEndTimestamp, note.endedAt);
  if (!end || end === start) return formatDateTime(start);
  return `${formatDateTime(start)} - ${formatDateTime(end)}`;
};

const dailyNoteDateFor = (note = {}) =>
  firstText(
    note.noteDate,
    note.date,
    note.dateAdded,
    note.rangeStartTimestamp,
    note.startTimestamp,
    note.startedAt,
    note.happenedAt,
    note.occurredAt,
    note.createdAt,
    note.updatedAt,
  );

const notePhotos = (note = {}) =>
  [
    ...(Array.isArray(note.photos) ? note.photos : []),
    ...(Array.isArray(note.attachments) ? note.attachments : []),
    ...(Array.isArray(note.files) ? note.files : []),
  ];

const notePhotoUrl = (photo = {}) =>
  firstText(photo.previewUrl, photo.signedUrl, photo.downloadUrl, photo.url, photo.fileUrl);

const notePhotoName = (photo = {}, index = 0) =>
  firstText(photo.description, photo.caption, photo.fileName, photo.name, `Attachment ${index + 1}`);

const normalizeDailyNoteQuickSelections = (items = []) =>
  (Array.isArray(items) ? items : []).map((item) => (
    typeof item === "string" ? { label: item } : item
  )).filter((item) => item?.label);

const dailyNoteQuickSelectionText = (selection = {}) =>
  [
    selection.label,
    selection.frequency ? `frequency: ${selection.frequency}` : "",
    selection.magnitude ? `magnitude: ${selection.magnitude}` : "",
    selection.trend ? `trend: ${selection.trend}` : "",
  ].filter(Boolean).join(" - ");

const drawerRowsFromRecords = (records = [], mapper, emptyLabel = "No records found.") => {
  const rows = sortedRecordRows(records.map(mapper)).slice(0, 6).map((record) => [
    recordTitle(record),
    [recordSummary(record), record.dateAdded ? formatDateTime(record.dateAdded) : ""].filter(Boolean).join(" | "),
  ]);
  return rows.length ? rows : [["No records found", emptyLabel]];
};

const drawerFieldValue = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  return value === null || value === undefined ? "" : String(value).trim();
};

const compactDrawerRows = (rows = [], emptyText = "No additional details recorded.") => {
  const compactRows = rows
    .map(([label, value]) => [label, drawerFieldValue(value)])
    .filter(([, value]) => value);
  return compactRows.length ? compactRows : [["Details", emptyText]];
};

const careTeamMemberName = (member = {}) =>
  firstText(
    member.name,
    member.caregiverName,
    member.careGiverName,
    member.careProfileName,
    member.title,
    member.staffName,
    member.userProfile?.name,
    member.user?.name,
    member.caregiver?.name,
    member.careGiver?.name,
    member.profile?.fullName,
    member.profileData?.fullName,
    [member.firstName, member.middleName, member.lastName].filter(Boolean).join(" "),
    [member.userProfile?.firstName, member.userProfile?.middleName, member.userProfile?.lastName].filter(Boolean).join(" "),
    [member.user?.firstName, member.user?.middleName, member.user?.lastName].filter(Boolean).join(" "),
    [member.caregiver?.firstName, member.caregiver?.middleName, member.caregiver?.lastName].filter(Boolean).join(" "),
    [member.careGiver?.firstName, member.careGiver?.middleName, member.careGiver?.lastName].filter(Boolean).join(" "),
    [member.profile?.firstName, member.profile?.middleName, member.profile?.lastName].filter(Boolean).join(" "),
    [member.profileData?.firstName, member.profileData?.middleName, member.profileData?.lastName].filter(Boolean).join(" "),
    member.organizationName,
    member.email,
    member.caregiverEmail,
    "Care staff",
  );

const careTeamRole = (member = {}) =>
  labelize(firstText(member.role, member.assignmentRole, member.relationship, member.contactType, "Care Team"));

const careTeamPhone = (member = {}) =>
  firstText(member.phone, member.mobilePhone, member.primaryPhone, member.phones?.preferred, member.phones?.mobile, member.phones?.office, member.phones?.home);

const careTeamEmail = (member = {}) =>
  firstText(member.email, member.caregiverEmail, member.careGiverEmail, member.primaryEmail, member.workEmail, member.userProfile?.email, member.user?.email, member.caregiver?.email, member.careGiver?.email, member.profile?.email, member.profileData?.email);

const staffAssignmentLabel = (member = {}) => {
  const name = careTeamMemberName(member);
  const role = careTeamRole(member);
  const shift = firstText(member.shift, [member.from, member.to].filter(Boolean).join(" - "));
  return [name, role && role !== "Care Team" ? `(${role})` : "", shift ? `- ${shift}` : ""].filter(Boolean).join(" ");
};

const staffDrawerId = (member = {}) =>
  firstText(member.key, member.id, member.careProfileId, member.profileId, member.caregiverUserId, member.userProfileId, member.email, member.caregiverEmail);

const careTeamSectionFor = (member = {}, index = 0, sourceLabel = "Care Team") => ({
  title: `${sourceLabel}: ${careTeamMemberName(member)}`,
  rows: compactDrawerRows([
    ["Name", careTeamMemberName(member)],
    ["Role / relationship", careTeamRole(member)],
    ["Organization", member.organizationName],
    ["Assignment", firstText(member.assignment, member.description, member.summary)],
    ["Shift", firstText(member.shift, [member.from, member.to].filter(Boolean).join(" - "))],
    ["Phone", careTeamPhone(member) || phonesSummary(member.phones)],
    ["Email", careTeamEmail(member)],
    ["Address", addressSummary(member.address)],
    ["Primary contact", member.primaryContact],
    ["Emergency contact", member.emergencyContact],
    ["Decision maker", member.decisionMaker],
    ["Next of kin", member.nextOfKin],
    ["Added", recordDate(member) ? formatDate(recordDate(member)) : ""],
    ["Added by", firstText(member.addedByName, member.createdByName, member.uploadedByName, member.assessorName, member.addedBy, member.createdBy)],
    ["Notes", member.notes],
  ]),
  sortKey: recordDate(member) || `${sourceLabel}-${index}`,
});

const residentClockStaffId = (member = {}) =>
  firstText(
    member.careProfileId,
    member.caregiverProfileId,
    member.careGiverProfileId,
    member.profileId,
    member.caregiverUserId,
    member.careGiverUserId,
    member.userProfileId,
    member.caregiverId,
    member.careGiverId,
    member.userProfile?.id,
    member.user?.id,
    member.caregiver?.id,
    member.careGiver?.id,
    member.id,
    member.email,
    member.caregiverEmail,
  );

const residentClockStaffSnapshot = (member = {}) => ({
  staffId: residentClockStaffId(member),
  name: careTeamMemberName(member),
  email: careTeamEmail(member),
  role: careTeamRole(member),
});

const residentClockStaffList = (record = {}) =>
  asList(record.accompanyingStaff || record.accompanyingStaffMembers || record.staff || record.staffMembers);

const residentClockStaffIds = (record = {}) =>
  asList(record.accompanyingStaffIds || record.staffIds || record.accompanyingCareStaffIds || record.careStaffIds);

const residentClockStaffNames = (record = {}) => {
  const staff = residentClockStaffList(record);
  if (staff.length) {
    return staff
      .map((member) => {
        if (typeof member === "string") return member;
        return [member.name || member.staffName || member.caregiverName || member.careGiverName, member.email || member.caregiverEmail || member.careGiverEmail]
          .filter(Boolean)
          .join(" - ");
      })
      .filter(Boolean)
      .join(", ");
  }
  const names = asList(record.accompanyingStaffNames || record.staffNames || record.accompanyingCareStaffNames);
  if (names.length) return names.join(", ");
  const ids = residentClockStaffIds(record);
  return ids.length ? ids.join(", ") : "Not added";
};

const mergeResidentClockOutRecord = (saved, payload) => {
  if (!saved || typeof saved !== "object") return { ...payload, id: `clock-${Date.now()}` };
  const merged = { ...payload, ...saved };
  if (!residentClockStaffList(merged).length && residentClockStaffList(payload).length) merged.accompanyingStaff = payload.accompanyingStaff;
  if (!residentClockStaffIds(merged).length && residentClockStaffIds(payload).length) merged.accompanyingStaffIds = payload.accompanyingStaffIds;
  return merged;
};

function ResidentClockInOutTab({
  residentId,
  residentName,
  records = [],
  currentRecord = null,
  loading = false,
  error = "",
  staffOptions = [],
  actorId = "",
  actorName = "",
  workspaceId = "",
  onRecordsChange,
  onCurrentRecordChange,
}) {
  const { appConfig, loading: appConfigLoading } = useAppConfig();
  const googleMapsApiKey =
    appConfig?.googleMapsApiKey ||
    appConfig?.googleMapsApiMatrixKey ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    process.env.VITE_GOOGLE_MAPS_API_KEY ||
    "";
  const staffChoices = staffOptions
    .map((staff) => ({ ...staff, _clockStaffId: residentClockStaffId(staff) }))
    .filter((staff, index, list) => staff._clockStaffId && list.findIndex((item) => item._clockStaffId === staff._clockStaffId) === index);
  const [clockOutForm, setClockOutForm] = useState({
    reason: "",
    otherReason: "",
    description: "",
    clockOutTime: dateTimeLocalValue(),
    expectedBackTime: "",
    destinationLocationAddress: "",
    emergencyContactPhone: "",
    staffIds: [],
  });
  const [clockInForm, setClockInForm] = useState({
    clockInTime: dateTimeLocalValue(),
    description: "",
  });
  const [clockDrawer, setClockDrawer] = useState("");
  const [saving, setSaving] = useState(false);
  const activeRecord = isResidentClockedOut(currentRecord) ? currentRecord : records.find(isResidentClockedOut) || null;
  const isOut = Boolean(activeRecord);
  const sortedRecords = [...records].sort((a, b) => new Date(residentClockOutTime(b) || 0) - new Date(residentClockOutTime(a) || 0));
  const selectedStaffIds = clockOutForm.staffIds;
  const selectedStaff = staffChoices.filter((staff) => selectedStaffIds.includes(staff._clockStaffId));
  const selectedReason = clockOutForm.reason === "Other" ? clockOutForm.otherReason : clockOutForm.reason;

  const updateClockOut = (field, value) => setClockOutForm((current) => ({ ...current, [field]: value }));
  const updateClockIn = (field, value) => setClockInForm((current) => ({ ...current, [field]: value }));

  const openClockOutDrawer = () => {
    setClockOutForm((current) => ({ ...current, clockOutTime: dateTimeLocalValue() }));
    setClockDrawer("out");
  };

  const openClockInDrawer = () => {
    setClockInForm((current) => ({ ...current, clockInTime: dateTimeLocalValue() }));
    setClockDrawer("in");
  };

  const closeClockDrawer = () => {
    if (saving) return;
    setClockDrawer("");
  };

  const submitClockOut = async (event) => {
    event.preventDefault();
    if (!residentId) return;
    if (!String(selectedReason || "").trim()) {
      message.error("Select or enter a reason before checking the resident out.");
      return;
    }
    if (!clockOutForm.clockOutTime) {
      message.error("Check-out time is required.");
      return;
    }
    setSaving(true);
    const payload = compactObject({
      residentId,
      residentName,
      workspaceId,
      reason: String(selectedReason).trim(),
      description: String(clockOutForm.description || "").trim(),
      clockOutTime: isoFromDateTimeLocal(clockOutForm.clockOutTime),
      expectedBackTime: isoFromDateTimeLocal(clockOutForm.expectedBackTime),
      destinationLocationAddress: String(clockOutForm.destinationLocationAddress || "").trim(),
      emergencyContactPhone: String(clockOutForm.emergencyContactPhone || "").trim(),
      accompanyingStaffIds: selectedStaffIds,
      accompanyingStaff: selectedStaff.map(residentClockStaffSnapshot),
      createdByUserId: actorId,
      createdByName: actorName,
      status: "CLOCKED_OUT",
    });
    try {
      const saved = await careService.residentClockInOut.clockOutForResident(residentId, payload, compactObject({ workspaceId, wid: workspaceId }));
      const record = mergeResidentClockOutRecord(saved, payload);
      onCurrentRecordChange?.(record);
      onRecordsChange?.((current) => [record, ...current.filter((item) => item.id !== record.id)]);
      setClockOutForm({
        reason: "",
        otherReason: "",
        description: "",
        clockOutTime: dateTimeLocalValue(),
        expectedBackTime: "",
        destinationLocationAddress: "",
        emergencyContactPhone: "",
        staffIds: [],
      });
      setClockDrawer("");
      message.success(`${residentName} checked out.`);
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to check resident out.");
    } finally {
      setSaving(false);
    }
  };

  const submitClockIn = async (event) => {
    event.preventDefault();
    if (!activeRecord?.id) return;
    if (!clockInForm.clockInTime) {
      message.error("Check-in time is required.");
      return;
    }
    setSaving(true);
    const payload = compactObject({
      residentId,
      workspaceId,
      clockInTime: isoFromDateTimeLocal(clockInForm.clockInTime),
      description: String(clockInForm.description || "").trim(),
      updatedByUserId: actorId,
      updatedByName: actorName,
      status: "CLOCKED_IN",
    });
    try {
      const saved = await careService.residentClockInOut.clockIn(activeRecord.id, payload, compactObject({ workspaceId, wid: workspaceId }));
      const record = saved && typeof saved === "object" ? saved : { ...activeRecord, ...payload };
      onCurrentRecordChange?.(null);
      onRecordsChange?.((current) => [record, ...current.filter((item) => item.id !== activeRecord.id)]);
      setClockInForm({ clockInTime: dateTimeLocalValue(), description: "" });
      setClockDrawer("");
      message.success(`${residentName} checked in.`);
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to check resident in.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel title="Resident CheckInOut">
      <div className="resident-tab-list-head">
        <p>Track when a resident leaves the home, returns, why they left, and which staff accompanied them.</p>
        <div className="resident-tab-list-actions">
          {isOut ? (
            <button className="mini-btn" type="button" onClick={openClockInDrawer}>
              <i className="ti ti-clock-check" /> Check In
            </button>
          ) : (
            <button className="mini-btn" type="button" onClick={openClockOutDrawer}>
              <i className="ti ti-clock-off" /> Check Out
            </button>
          )}
        </div>
      </div>
      {error ? <p className="form-error">{error}</p> : null}
      <section className={`resident-clock-status-card${isOut ? " is-out" : ""}`}>
        <span className="round-icon blue"><i className={`ti ${isOut ? "ti-clock-off" : "ti-clock-check"}`} /></span>
        <div>
          <small>Current status</small>
          <strong>{isOut ? "Checked out" : "In the home"}</strong>
          <p>{isOut ? `${residentName} left at ${formatDateTime(residentClockOutTime(activeRecord))}. Expected back ${formatDateTime(residentExpectedBackTime(activeRecord))}.` : "No active resident check-out record."}</p>
        </div>
      </section>

      <div className="resident-record-section-title">Resident CheckInOut Records</div>
      <div className="resident-tab-list" style={{ "--resident-record-columns": "150px 150px 150px 150px minmax(180px, 1fr) 160px minmax(220px, 1fr) minmax(220px, 1fr)" }}>
        <div className="resident-tab-list-row resident-tab-list-head-row">
          <span>Check out</span>
          <span>Expected back</span>
          <span>Check in</span>
          <span>Reason</span>
          <span>Destination</span>
          <span>Emergency phone</span>
          <span>Accompanying staff</span>
          <span>Description</span>
        </div>
        {sortedRecords.map((record) => (
          <article className="resident-tab-list-row" key={record.id || `${residentClockOutTime(record)}-${residentClockInTime(record)}`}>
            <span>{formatDateTime(residentClockOutTime(record))}</span>
            <span>{formatDateTime(residentExpectedBackTime(record))}</span>
            <span>{formatDateTime(residentClockInTime(record))}</span>
            <span><em className="pill blue">{record.reason || "Not added"}</em></span>
            <span>{residentClockDestinationAddress(record) || "Not added"}</span>
            <span>{residentClockEmergencyContactPhone(record) || "Not added"}</span>
            <span>{residentClockStaffNames(record)}</span>
            <span>{record.description || record.clockOutDescription || record.clockInDescription || "No details added."}</span>
          </article>
        ))}
        {loading ? <div className="resident-inline-empty">Loading resident CheckInOut records...</div> : null}
        {!loading && !sortedRecords.length ? <div className="resident-inline-empty">No resident CheckInOut records found.</div> : null}
      </div>

      <Drawer
        title={clockDrawer === "in" ? "Check Resident In" : "Check Resident Out"}
        placement="right"
        open={Boolean(clockDrawer)}
        onClose={closeClockDrawer}
        width={620}
        className="resident-action-drawer"
      >
        {clockDrawer === "in" ? (
          <form className="assessment-drawer-form resident-clock-form resident-clockout-drawer-form" onSubmit={submitClockIn}>
            <section className="assessment-drawer-section resident-clockout-context">
              <div>
                <span>Resident CheckInOut</span>
                <strong>{residentName}</strong>
                <small>Record the return time for this resident.</small>
              </div>
              <i className="ti ti-clock-check" />
            </section>
            <section className="assessment-drawer-section resident-clockout-section">
              <h3>Check-in Details</h3>
              <div className="assessment-drawer-grid">
                <label className="ncp-form-field">
                  <span>Check-in time</span>
                  <input type="datetime-local" value={clockInForm.clockInTime} onChange={(event) => updateClockIn("clockInTime", event.target.value)} required />
                </label>
                <label className="ncp-form-field wide">
                  <span>Description</span>
                  <textarea value={clockInForm.description} onChange={(event) => updateClockIn("description", event.target.value)} placeholder="Optional return details" rows={3} />
                </label>
              </div>
            </section>
            <div className="assessment-drawer-actions">
              <button className="btn secondary" type="button" onClick={closeClockDrawer} disabled={saving}>Cancel</button>
              <button className="btn primary" type="submit" disabled={saving}>
                <i className={`ti ${saving ? "ti-loader-2 spin" : "ti-clock-check"}`} /> {saving ? "Saving..." : "Check In"}
              </button>
            </div>
          </form>
        ) : (
          <form className="assessment-drawer-form resident-clock-form resident-clockout-drawer-form" onSubmit={submitClockOut}>
            <section className="assessment-drawer-section resident-clockout-context">
              <div>
                <span>Resident CheckInOut</span>
                <strong>{residentName}</strong>
                <small>Record resident movement out of the home.</small>
              </div>
              <i className="ti ti-clock-off" />
            </section>
            <section className="assessment-drawer-section resident-clockout-section">
              <h3>Check-out Details</h3>
              <div className="assessment-drawer-grid">
                <label className="ncp-form-field wide">
                  <span>Reason</span>
                  <select value={clockOutForm.reason} onChange={(event) => updateClockOut("reason", event.target.value)} required>
                    <option value="" disabled>Select reason</option>
                    {residentClockReasonOptions.map((reason) => <option key={reason} value={reason}>{reason}</option>)}
                  </select>
                </label>
                {clockOutForm.reason === "Other" ? (
                  <label className="ncp-form-field wide">
                    <span>Specify reason</span>
                    <input value={clockOutForm.otherReason} onChange={(event) => updateClockOut("otherReason", event.target.value)} placeholder="Reason for leaving" required />
                  </label>
                ) : null}
                <label className="ncp-form-field">
                  <span>Check-out time</span>
                  <strong className="resident-checkinout-system-time">{formatDateTime(isoFromDateTimeLocal(clockOutForm.clockOutTime))}</strong>
                  <input type="hidden" value={clockOutForm.clockOutTime} readOnly />
                </label>
                <label className="ncp-form-field">
                  <span>Time expected back</span>
                  <input type="datetime-local" value={clockOutForm.expectedBackTime} onChange={(event) => updateClockOut("expectedBackTime", event.target.value)} />
                </label>
                <label className="ncp-form-field wide">
                  <span>Destination location address</span>
                  {appConfigLoading && !googleMapsApiKey ? (
                    <input type="text" disabled placeholder="Loading map address search..." />
                  ) : (
                    <GooglePlacesAutocompleteInput
                      googleMapsApiKey={googleMapsApiKey}
                      placeholder={googleMapsApiKey ? "Search destination address" : "Enter destination address"}
                      types={["address"]}
                      value={clockOutForm.destinationLocationAddress}
                      onChange={(value) => updateClockOut("destinationLocationAddress", value)}
                      onResolvePlace={({ details, prediction }) => {
                        updateClockOut("destinationLocationAddress", details?.formatted_address || prediction?.description || "");
                      }}
                    />
                  )}
                </label>
                <label className="ncp-form-field wide">
                  <span>Emergency contact phone</span>
                  <input type="tel" value={clockOutForm.emergencyContactPhone} onChange={(event) => updateClockOut("emergencyContactPhone", event.target.value)} placeholder="Optional phone number for urgent contact" />
                </label>
                <label className="ncp-form-field wide">
                  <span>Description</span>
                  <textarea value={clockOutForm.description} onChange={(event) => updateClockOut("description", event.target.value)} placeholder="Optional details about where they are going, appointment notes, transportation, or instructions." rows={3} />
                </label>
              </div>
            </section>
            <section className="assessment-drawer-section resident-clockout-section">
              <h3>Accompanying Staff</h3>
              <label className="ncp-form-field wide">
                <span>Search staff</span>
                <Select
                  className="resident-clockout-select resident-clockout-staff-select"
                  mode="multiple"
                  value={clockOutForm.staffIds}
                  showSearch
                  placeholder="Search accompanying staff"
                  optionFilterProp="label"
                  onChange={(values) => updateClockOut("staffIds", values)}
                  options={staffChoices.map((staff) => ({
                    value: staff._clockStaffId,
                    label: [careTeamMemberName(staff), careTeamEmail(staff)].filter(Boolean).join(" - "),
                  }))}
                />
              </label>
              {!staffChoices.length ? <div className="resident-inline-empty">No assigned staff found for this resident.</div> : null}
              {selectedStaff.length ? (
                <div className="resident-clockout-selected-staff">
                  {selectedStaff.map((staff) => (
                    <span key={staff._clockStaffId}>
                      <strong>{careTeamMemberName(staff)}</strong>
                      <small>{careTeamEmail(staff) || "No email added"}</small>
                    </span>
                  ))}
                </div>
              ) : null}
            </section>
            <div className="assessment-drawer-actions">
              <button className="btn secondary" type="button" onClick={closeClockDrawer} disabled={saving}>Cancel</button>
              <button className="btn primary" type="submit" disabled={saving}>
                <i className={`ti ${saving ? "ti-loader-2 spin" : "ti-clock-off"}`} /> {saving ? "Saving..." : "Check Out"}
              </button>
            </div>
          </form>
        )}
      </Drawer>
    </Panel>
  );
}

const isWithinDateRange = (date, startDate, endDate) => {
  if (!date) return false;
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return false;
  const target = dateInputValue(date);
  return target >= startDate && target <= endDate;
};

function RecordValue({ record, column }) {
  const [, key, type] = column;
  const value = record[key];

  if (type === "date") {
    return <span>{key === "dateAdded" ? formatDateTime(value) : formatDate(value)}</span>;
  }

  if (type === "files") {
    return (
      <span className="assessment-file-stack">
        {value.map((file) => (
          <small key={file}><i className="ti ti-paperclip" /> {file}</small>
        ))}
      </span>
    );
  }

  if (type === "attachedRecords") {
    return (
      <span className="assessment-record-stack">
        {value.map((record) => (
          <small key={record}>{record}</small>
        ))}
      </span>
    );
  }

  if (type === "status") {
    return <em>{value}</em>;
  }

  if (key === "period" || key === "processName" || key === "title") {
    return <span><strong>{value}</strong>{record.summary ? <small>{record.summary}</small> : null}</span>;
  }

  return <span>{value}</span>;
}

function NcpField({ field }) {
  const [label, name, type, options = []] = field;

  if (type === "textarea") {
    return (
      <label className="ncp-form-field ncp-form-field-wide">
        <span>{label}</span>
        <textarea name={name} rows={3} />
      </label>
    );
  }

  if (type === "select") {
    return (
      <label className="ncp-form-field">
        <span>{label}</span>
        <select name={name}>
          <option value="">Select</option>
          {options.map((option) => (
            <option value={option} key={option}>{option}</option>
          ))}
        </select>
      </label>
    );
  }

  if (type === "checkboxes") {
    return (
      <fieldset className="ncp-form-field ncp-form-field-wide">
        <legend>{label}</legend>
        <div className="ncp-checkbox-grid">
          {options.map((option) => (
            <label key={option}>
              <input type="checkbox" name={`${name}-${option}`} />
              <span>{option}</span>
            </label>
          ))}
        </div>
      </fieldset>
    );
  }

  return (
    <label className="ncp-form-field">
      <span>{label}</span>
      <input name={name} type={type} />
    </label>
  );
}

const assessmentFormSections = [
  {
    title: "Assessment Process",
    fields: [
      ["Process name", "processName", "text"],
      ["Patient type", "patientType", "select", ["NEW", "RESIDENT"]],
      ["Start date", "processStartDate", "date"],
      ["End date", "processEndDate", "date"],
      ["Process status", "processStatus", "select", ["Draft", "Active", "In review", "Confirmed", "Closed"]],
      ["Assessment process owner", "processOwner", "text"],
      ["Attached process records", "attachedProcessRecords", "checkboxes", ["Initial Assessment", "Meet and Greet", "Initial Assessment Review", "Client Onboarding", "Resident 30-Day Probation", "Notes", "Preliminary Plan", "Negotiated Plan", "Final Assessment", "Residency Confirmation"]],
    ],
  },
  {
    title: "Assessment Source",
    fields: [
      ["Assessment type", "assessmentType", "select", ["Initial state assessment", "Annual assessment", "Change-of-condition review", "Final assessment update", "Other"]],
      ["Assessment version", "assessmentVersion", "text"],
      ["Assessment date", "assessmentDate", "date"],
      ["Date received", "dateReceived", "date"],
      ["Social worker", "socialWorker", "text"],
      ["State case ID", "stateCaseId", "text"],
    ],
  },
  {
    title: "Resident and Care Snapshot",
    fields: [
      ["Resident name", "residentName", "text"],
      ["Medication behaviors", "medicationBehaviors", "textarea"],
      ["Diagnosis and history", "diagnosisHistory", "textarea"],
      ["Care needed", "careNeeded", "textarea"],
      ["Medication information", "medicationInformation", "textarea"],
      ["Client information notes", "clientInformation", "textarea"],
    ],
  },
  {
    title: "Attachments and Review",
    fields: [
      ["Assessment file", "assessmentFile", "file"],
      ["Medication profile file", "medicationProfileFile", "file"],
      ["Supporting files", "supportingFiles", "file"],
      ["Imported from state assessment", "importedFromState", "select", ["Yes", "No"]],
      ["Reviewed by", "reviewedBy", "text"],
      ["Review status", "reviewStatus", "select", ["Draft", "Ready for provider review", "Reviewed", "Needs follow-up"]],
      ["Provider review notes", "providerReviewNotes", "textarea"],
    ],
  },
];

function AssessmentDrawerForm() {
  return (
    <form className="assessment-drawer-form">
      {assessmentFormSections.map((section) => (
        <section className="assessment-drawer-section" key={section.title}>
          <h3>{section.title}</h3>
          <div className="assessment-drawer-grid">
            {section.fields.map((field) => (
              <NcpField field={field} key={field[1]} />
            ))}
          </div>
        </section>
      ))}
      <div className="assessment-drawer-actions">
        <button className="btn secondary" type="button">Save draft</button>
        <button className="btn primary" type="button">Add assessment</button>
      </div>
    </form>
  );
}

function DrawerEditField({ label, name, value, sectionTitle = "" }) {
  const textValue = displayValue(value, "");
  const isReadonly = sectionTitle === "Admission Timeline" || /^(Admission date|Discharge date)$/i.test(label);
  const isLong = textValue.length > 80 || /notes|address|summary|description|details|care|behavior|assignment/i.test(label);
  if (isReadonly) {
    return (
      <div className="ncp-form-field resident-readonly-form-field">
        <span>{label}</span>
        <strong>{textValue || "Not set"}</strong>
        <small>Managed from the resident admission workflow</small>
      </div>
    );
  }
  return (
    <label className={`ncp-form-field ${isLong ? "ncp-form-field-wide" : ""}`}>
      <span>{label}</span>
      {isLong ? (
        <textarea defaultValue={textValue} name={name} rows={3} />
      ) : (
        <input defaultValue={textValue} name={name} />
      )}
    </label>
  );
}

function AssignedStaffPicker({ picker }) {
  if (!picker) return null;
  const selectedIds = new Set((picker.selected || []).map(careProfileOptionId).filter(Boolean));
  const visibleOptions = (picker.options || []).filter((profile) => careProfileOptionId(profile));
  const availableOptions = visibleOptions.filter((profile) => !selectedIds.has(careProfileOptionId(profile)));

  return (
    <section className="assessment-drawer-section assigned-staff-picker">
      <div className="assessment-section-heading-row">
        <h3>Assigned Staff</h3>
        <span>{(picker.selected || []).length} selected</span>
      </div>
      <label className="ncp-form-field ncp-form-field-wide">
        <span>Search care givers</span>
        <Select
          allowClear
          className="assigned-staff-select"
          filterOption={(input, option) => String(option?.searchText || "").toLowerCase().includes(input.toLowerCase())}
          loading={picker.loading}
          notFoundContent={picker.loading ? "Loading care givers..." : "No care givers found"}
          onChange={(optionId) => {
            const selectedProfile = visibleOptions.find((profile) => careProfileOptionId(profile) === optionId);
            if (selectedProfile) picker.onToggle(selectedProfile);
          }}
          onSearch={picker.onSearchChange}
          optionFilterProp="searchText"
          options={availableOptions.map((profile) => {
            const optionId = careProfileOptionId(profile);
            const optionName = careProfileOptionName(profile);
            const optionEmail = careProfileOptionEmail(profile);
            return {
              label: (
                <span className="assigned-staff-select-option">
                  <strong>{optionName}</strong>
                  <small>{optionEmail}</small>
                </span>
              ),
              searchText: `${optionName} ${optionEmail}`,
              value: optionId,
            };
          })}
          placeholder="Select care giver"
          showSearch
          value={null}
        />
      </label>
      <div className="assigned-staff-selected-list">
        {(picker.selected || []).length ? (
          picker.selected.map((profile) => {
            const optionId = careProfileOptionId(profile);
            return (
              <button className="assigned-staff-selected-pill" key={optionId || careProfileOptionName(profile)} type="button" onClick={() => picker.onRemove(optionId)}>
                <span>
                  <strong>{careProfileOptionName(profile)}</strong>
                  <small>{careProfileOptionEmail(profile)}</small>
                </span>
                <i className="ti ti-x" />
              </button>
            );
          })
        ) : (
          <p className="probation-empty">No staff selected yet.</p>
        )}
      </div>
      {!picker.loading && !visibleOptions.length ? (
        <div className="resident-inline-empty">No care givers found.</div>
      ) : null}
    </section>
  );
}

function DrawerEditSections({ sections = [], staffPicker = null, onCancel = null, onSave = null, saving = false }) {
  return (
    <form
      className="assessment-drawer-form resident-edit-drawer-form"
      onSubmit={(event) => {
        event.preventDefault();
        onSave?.(Object.fromEntries(new FormData(event.currentTarget).entries()));
      }}
    >
      {sections.map((section) => (
        <section className="assessment-drawer-section" key={section.title}>
          <h3>{section.title}</h3>
          <div className="assessment-drawer-grid">
            {(section.rows || []).map(([label, value, options = {}]) => (
              <DrawerEditField label={label} name={options.name} sectionTitle={section.title} value={value} key={label} />
            ))}
          </div>
        </section>
      ))}
      <AssignedStaffPicker picker={staffPicker} />
      <div className="assessment-drawer-actions">
        <button className="btn secondary" type="button" disabled={saving} onClick={onCancel}>Cancel</button>
        <button className="btn primary" type="submit" disabled={saving || !onSave}>{saving ? "Saving..." : "Save changes"}</button>
      </div>
    </form>
  );
}

function DrawerDetailSections({ sections = [] }) {
  return (
    <div className="resident-drawer-list persona-drawer-list">
      {sections.map((section) => (
        <article key={section.title}>
          <strong>{section.title}</strong>
          <dl>
            {(section.rows || []).map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>
                  {label === "Care profile" && displayValue(value, "") ? (
                    <Link className="resident-drawer-profile-link" to={`/staff/${encodeURIComponent(displayValue(value, ""))}`}>
                      {displayValue(value, "Not set")}
                    </Link>
                  ) : (
                    displayValue(value, "Not set")
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </article>
      ))}
    </div>
  );
}

function ResidentTabList({
  title,
  intro,
  tab,
  onCreate,
  rowsOverride,
  currentOverride,
  loading = false,
  error = "",
  showCreate = true,
  activeAssessmentId = "",
  assessmentRecordType = "",
  residentName = "Resident",
  residentBirthDate = "",
  residentId = "",
  assessmentNotesPath = "",
  capturedByName = "",
  capturedByUserId = "",
  capturedByUser = null,
  onDailyNoteSaved = null,
  onEdit = null,
}) {
  const config = tabItems[tab];
  const navigate = useNavigate();
  const [noteDateRange, setNoteDateRange] = useState({ start: yesterdayDate, end: todayDate });
  const [selectedDailyNote, setSelectedDailyNote] = useState(null);
  const [dailyNoteDrawerOpen, setDailyNoteDrawerOpen] = useState(false);
  const [dailyNoteSaving, setDailyNoteSaving] = useState(false);
  const [dailyNoteDate, setDailyNoteDate] = useState(todayDate);
  const [dailyNoteApproximateTime, setDailyNoteApproximateTime] = useState(timeInputValue(new Date()));
  const [dailyNoteEndTime, setDailyNoteEndTime] = useState(timeInputValue(shiftMinutes(new Date(), 15)));
  const [dailyNoteCategory, setDailyNoteCategory] = useState("Behavior");
  const [dailyNoteOtherCategory, setDailyNoteOtherCategory] = useState("");
  const [dailyNoteSelectionMode, setDailyNoteSelectionMode] = useState("SEARCH");
  const [dailyNoteSelectionQuery, setDailyNoteSelectionQuery] = useState("");
  const [showAllDailyNoteSelections, setShowAllDailyNoteSelections] = useState(false);
  const [dailyNoteQuickSelections, setDailyNoteQuickSelections] = useState([]);
  const [dailyNoteRichText, setDailyNoteRichText] = useState("");
  const [dailyNoteInterventionAction, setDailyNoteInterventionAction] = useState("");
  const [dailyNotePhotos, setDailyNotePhotos] = useState([]);
  const isDailyNotesTab = tab === "daily-notes";
  const sourceRows = rowsOverride || config.rows;
  const sourceCurrent = currentOverride === undefined ? config.current : currentOverride;
  const rows = sortedRecordRows(sourceRows).filter((record) =>
    !isDailyNotesTab || isWithinDateRange(dailyNoteDateFor(record), noteDateRange.start, noteDateRange.end)
  );
  const currentRecord = sourceCurrent && (!isDailyNotesTab || isWithinDateRange(dailyNoteDateFor(sourceCurrent), noteDateRange.start, noteDateRange.end))
    ? sourceCurrent
    : null;
  const dailyNoteSearchActive = dailyNoteSelectionMode === "SEARCH";
  const visibleDailyNoteSelectionGroups = useMemo(
    () => {
      if (dailyNoteSearchActive && !dailyNoteSelectionQuery.trim()) return [];
      return dailyNoteSelectionGroups(
        dailyNoteQuickSelectionsByCategory,
        dailyNoteCategory,
        dailyNoteSearchActive ? dailyNoteSelectionQuery : "",
        showAllDailyNoteSelections,
      );
    },
    [dailyNoteCategory, dailyNoteSearchActive, dailyNoteSelectionQuery, showAllDailyNoteSelections],
  );
  const dailyNoteSelectionTotal = useMemo(
    () => dailyNoteSearchActive && !dailyNoteSelectionQuery.trim()
      ? 0
      : dailyNoteSelectionCount(
          dailyNoteQuickSelectionsByCategory,
          dailyNoteCategory,
          dailyNoteSearchActive ? dailyNoteSelectionQuery : "",
        ),
    [dailyNoteCategory, dailyNoteSearchActive, dailyNoteSelectionQuery],
  );

  const openCreate = () => {
    if (isDailyNotesTab) {
      return;
    }
    onCreate(config.createDrawerKey || config.drawerKey);
  };
  const openCurrent = () => {
    if (isDailyNotesTab) {
      return;
    }
    if (currentRecord?.routePath) {
      navigate(currentRecord.routePath);
      return;
    }
    onCreate(config.drawerKey);
  };
  const toggleDailyNoteQuickSelection = (selection) => {
    const selectionLabel = typeof selection === "string" ? selection : selection?.label;
    if (!selectionLabel) return;
    if (typeof selection === "object" && selection?.category) {
      setDailyNoteCategory(selection.category);
      setDailyNoteOtherCategory("");
    }
    setDailyNoteQuickSelections((current) =>
      current.some((item) => item.label === selectionLabel)
        ? current.filter((item) => item.label !== selectionLabel)
        : [...current, { label: selectionLabel }]
    );
  };
  const updateDailyNoteQuickSelectionDetail = (label, field, value) => {
    setDailyNoteQuickSelections((current) =>
      current.map((item) => (item.label === label ? { ...item, [field]: value } : item))
    );
  };
  const addDailyNotePhotos = (event) => {
    const files = Array.from(event.target.files || []).map((file) => ({
      fileName: file.name,
      originalFileName: file.name,
      description: file.name,
      type: file.type,
      contentType: file.type,
      previewUrl: URL.createObjectURL(file),
    }));
    if (files.length) setDailyNotePhotos((current) => [...current, ...files]);
    event.target.value = "";
  };
  const resetDailyNoteForm = () => {
    setDailyNoteDate(todayDate);
    setDailyNoteApproximateTime(timeInputValue(new Date()));
    setDailyNoteEndTime(timeInputValue(shiftMinutes(new Date(), 15)));
    setDailyNoteCategory("Behavior");
    setDailyNoteOtherCategory("");
    setDailyNoteSelectionMode("SEARCH");
    setDailyNoteSelectionQuery("");
    setShowAllDailyNoteSelections(false);
    setDailyNoteQuickSelections([]);
    setDailyNoteRichText("");
    setDailyNoteInterventionAction("");
    setDailyNotePhotos([]);
  };
  const saveDailyNote = async () => {
    if (dailyNoteSaving) return;
    setDailyNoteSaving(true);
    const now = new Date().toISOString();
    const category = dailyNoteCategory === "Other" ? dailyNoteOtherCategory || "Other" : dailyNoteCategory;
    const typedSummary = stripHtml(dailyNoteRichText);
    const quickSummary = dailyNoteQuickSelections.map(dailyNoteQuickSelectionText).join("; ");
    const startTime = dailyNoteApproximateTime || timeInputValue(new Date());
    const endTime = dailyNoteEndTime || startTime;
    const rangeStartTimestamp = `${dailyNoteDate}T${startTime}:00`;
    const rangeEndTimestamp = `${dailyNoteDate}T${endTime}:00`;
    const caregiverDisplayName = capturedByName || personDisplayName(capturedByUser) || "Care giver";
    const notePayload = {
      id: `daily-note-${Date.now()}`,
      residentId,
      assessmentProcessId: activeAssessmentId,
      assessmentRecordType,
      recordType: assessmentRecordType,
      noteDate: rangeStartTimestamp,
      date: rangeStartTimestamp,
      rangeStartTimestamp,
      rangeEndTimestamp,
      startTimestamp: rangeStartTimestamp,
      endTimestamp: rangeEndTimestamp,
      startedAt: rangeStartTimestamp,
      endedAt: rangeEndTimestamp,
      happenedAt: rangeStartTimestamp,
      occurredAt: rangeStartTimestamp,
      approximateTime: startTime,
      startTime,
      endTime,
      timeIsApproximate: true,
      title: `${category} note`,
      category,
      quickSelections: dailyNoteQuickSelections,
      summary: typedSummary || quickSummary || "Daily note captured.",
      notes: dailyNoteRichText,
      interventionAction: dailyNoteInterventionAction,
      intervention: dailyNoteInterventionAction,
      actionTaken: dailyNoteInterventionAction,
      photos: dailyNotePhotos,
      capturedBy: caregiverDisplayName,
      capturedByName: caregiverDisplayName,
      capturedByUserId,
      capturedByUser,
      caregiverName: caregiverDisplayName,
      caregiverUserId: capturedByUserId,
      addedBy: caregiverDisplayName,
      addedByName: caregiverDisplayName,
      createdBy: caregiverDisplayName,
      createdByName: caregiverDisplayName,
      createdByUserId: capturedByUserId,
      updatedBy: caregiverDisplayName,
      updatedByName: caregiverDisplayName,
      updatedByUserId: capturedByUserId,
      updatedAt: now,
    };
    try {
      const response = activeAssessmentId && assessmentRecordType
        ? await careService.assessmentRecords.submitDailyNoteForProcess(activeAssessmentId, assessmentRecordType, { dailyNote: notePayload })
        : await careService.dailyNotes.create(notePayload);
      const payload = response?.payload && typeof response.payload === "object" ? response.payload : response;
      const savedNote = payload?.dailyNote || payload?.note || (payload?.id || payload?.noteDate || payload?.date ? payload : null) || notePayload;
      onDailyNoteSaved?.({ ...notePayload, ...(savedNote && typeof savedNote === "object" ? savedNote : {}) });
      resetDailyNoteForm();
      setDailyNoteDrawerOpen(false);
      message.success("Daily note saved.");
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to save daily note.");
    } finally {
      setDailyNoteSaving(false);
    }
  };
  const openRecord = (record) => {
    if (isDailyNotesTab) {
      setSelectedDailyNote(record);
      return;
    }
    if (record.routePath) {
      navigate(record.routePath);
      return;
    }
    onCreate(config.drawerKey);
  };
  return (
    <Panel title={title}>
      <div className="resident-tab-list-head">
        <p>{intro}</p>
        <div className="resident-tab-list-actions">
          {isDailyNotesTab && assessmentNotesPath ? (
            <Link className="mini-btn" to={assessmentNotesPath}>
              <i className="ti ti-clipboard-text" /> Open Behavioral Notes
            </Link>
          ) : null}
          {onEdit && !isDailyNotesTab ? (
            <button className="mini-btn" type="button" onClick={onEdit}>
              <i className="ti ti-pencil" /> Edit details
            </button>
          ) : null}
          {showCreate && !isDailyNotesTab ? (
            <button className="mini-btn" type="button" onClick={openCreate}>
              <i className="ti ti-plus" /> {config.createLabel}
            </button>
          ) : null}
        </div>
      </div>
      {isDailyNotesTab ? (
        <div className="resident-date-range">
          <label>
            <span>From</span>
            <input type="date" value={noteDateRange.start} max={noteDateRange.end} onChange={(event) => setNoteDateRange((current) => ({ ...current, start: event.target.value }))} />
          </label>
          <label>
            <span>To</span>
            <input type="date" value={noteDateRange.end} min={noteDateRange.start} onChange={(event) => setNoteDateRange((current) => ({ ...current, end: event.target.value }))} />
          </label>
        </div>
      ) : null}
      {error ? <p className="form-error">{error}</p> : null}
      {currentRecord ? (
        <button className="resident-active-item" type="button" onClick={openCurrent}>
          <span>
            <small>{config.currentLabel}</small>
            <strong>{recordTitle(currentRecord)}</strong>
            <em>{recordSummary(currentRecord)}</em>
            {isDailyNotesTab ? <small>Captured by {currentRecord.addedBy || "Provider"}</small> : null}
          </span>
          <b>{formatDateTime(currentRecord.dateAdded)}</b>
          <i>{recordBadge(currentRecord)}</i>
        </button>
      ) : null}
      <div className="resident-record-section-title">Previous records</div>
      <div className="resident-tab-list" style={{ "--resident-record-columns": config.gridTemplate }}>
        <div className="resident-tab-list-row resident-tab-list-head-row">
          {config.columns.map(([label]) => (
            <span key={label}>{label}</span>
          ))}
          <span />
        </div>
        {rows.map((record) => (
          <button
            className="resident-tab-list-row"
            type="button"
            onClick={() => {
              if (isDailyNotesTab) return;
              record.routePath ? navigate(record.routePath) : onCreate(config.drawerKey);
            }}
            key={`${tab}-${record.id || recordTitle(record)}-${record.dateAdded}`}
          >
            {config.columns.map((column) => (
              <RecordValue record={record} column={column} key={column[1]} />
            ))}
            <i className="ti ti-chevron-right" />
          </button>
        ))}
        {loading ? <div className="resident-inline-empty">Loading records...</div> : null}
        {!loading && !rows.length ? <div className="resident-inline-empty">No previous records found.</div> : null}
      </div>
      {isDailyNotesTab ? (
        <>
          <Drawer
            title="Add Daily Note"
            placement="right"
            open={dailyNoteDrawerOpen}
            onClose={() => setDailyNoteDrawerOpen(false)}
            width={760}
            className="resident-action-drawer daily-note-drawer"
          >
            <form
              className="assessment-drawer-form"
              onSubmit={(event) => {
                event.preventDefault();
                saveDailyNote();
              }}
            >
              <section className="assessment-drawer-section daily-note-resident-context">
                <div>
                  <span>Resident</span>
                  <strong>{residentName}</strong>
                </div>
                <div>
                  <span>Date of birth</span>
                  <strong>{residentBirthDate ? compactDate(residentBirthDate) : "Not added"}</strong>
                </div>
              </section>
              <section className="assessment-drawer-section">
                <h3>Note Details</h3>
                <div className="assessment-drawer-grid daily-note-details-grid">
                  <label className="ncp-form-field"><span>Note date</span><input type="date" value={dailyNoteDate} onChange={(event) => setDailyNoteDate(event.target.value)} /></label>
                  <label className="ncp-form-field"><span>Start time</span><input type="time" value={dailyNoteApproximateTime} onChange={(event) => setDailyNoteApproximateTime(event.target.value)} /></label>
                  <label className="ncp-form-field"><span>End time</span><input type="time" value={dailyNoteEndTime} onChange={(event) => setDailyNoteEndTime(event.target.value)} /></label>
                </div>
              </section>
              <section className="assessment-drawer-section">
                <div className="assessment-section-heading-row">
                  <h3>Quick Selections</h3>
                  <span>{dailyNoteQuickSelections.length} selected</span>
                </div>
                <div className="quick-note-mode-tabs" role="tablist" aria-label="Quick selection mode">
                  {["SEARCH", "SELECT"].map((mode) => (
                    <button
                      aria-selected={dailyNoteSelectionMode === mode}
                      className={dailyNoteSelectionMode === mode ? "active" : ""}
                      key={mode}
                      role="tab"
                      type="button"
                      onClick={() => {
                        setDailyNoteSelectionMode(mode);
                        setDailyNoteSelectionQuery("");
                        setShowAllDailyNoteSelections(false);
                      }}
                    >
                      {mode === "SELECT" ? "Select" : "Search"}
                    </button>
                  ))}
                </div>
                {dailyNoteSelectionMode === "SELECT" ? (
                  <div className="quick-note-search-panel quick-note-search-panel-single">
                    <label className="quick-note-category-field">
                      <span>Category</span>
                      <select value={dailyNoteCategory} onChange={(event) => {
                        setDailyNoteCategory(event.target.value);
                        setDailyNoteSelectionQuery("");
                        setShowAllDailyNoteSelections(false);
                        setDailyNoteQuickSelections([]);
                      }}>
                        {dailyNoteCategories.map((category) => <option key={category} value={category}>{category}</option>)}
                      </select>
                    </label>
                  </div>
                ) : (
                  <div className="quick-note-search-panel quick-note-search-panel-single">
                    <label className="quick-note-search-field">
                      <i className="ti ti-search" />
                      <input
                        value={dailyNoteSelectionQuery}
                        onChange={(event) => {
                          setDailyNoteSelectionQuery(event.target.value);
                          setShowAllDailyNoteSelections(false);
                        }}
                        placeholder="Search category or behavior"
                      />
                    </label>
                  </div>
                )}
                {dailyNoteCategory === "Other" ? (
                  <label className="ncp-form-field ncp-form-field-wide"><span>Specify category</span><input value={dailyNoteOtherCategory} onChange={(event) => setDailyNoteOtherCategory(event.target.value)} placeholder="Enter note category" /></label>
                ) : null}
                {visibleDailyNoteSelectionGroups.length ? (
                  <>
                    <div className={`quick-note-chip-grid${showAllDailyNoteSelections || dailyNoteSelectionQuery ? " is-expanded" : ""}`}>
                      {visibleDailyNoteSelectionGroups.map((group) => (
                        <div className="quick-note-tree-group" key={group.category}>
                          <strong>{group.category}</strong>
                          <div className="quick-note-tree-children">
                            {group.selections.map((selection) => (
                              <button className={`quick-note-chip ${dailyNoteQuickSelections.some((item) => item.label === selection.label) ? "selected" : ""}`} key={selection.path} type="button" onClick={() => toggleDailyNoteQuickSelection(selection)}>
                                {dailyNoteQuickSelections.some((item) => item.label === selection.label) ? <i className="ti ti-check" /> : null}
                                <span>{selection.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    {!dailyNoteSelectionQuery && dailyNoteSelectionTotal > DAILY_NOTE_VISIBLE_SELECTION_COUNT ? (
                      <button className="quick-note-view-more" type="button" onClick={() => setShowAllDailyNoteSelections((current) => !current)}>
                        {showAllDailyNoteSelections ? "Show less" : `View ${dailyNoteSelectionTotal - DAILY_NOTE_VISIBLE_SELECTION_COUNT} more`}
                      </button>
                    ) : null}
                  </>
                ) : <p className="probation-empty">{dailyNoteSelectionMode === "SEARCH" ? "Search for a category or behavior." : "Use the notes field for this category."}</p>}
                {dailyNoteQuickSelections.length ? (
                  <div className="quick-note-detail-list">
                    {dailyNoteQuickSelections.map((selection) => (
                      <div className="quick-note-detail-row" key={selection.label}>
                        <strong>{selection.label}</strong>
                        {["frequency", "magnitude", "trend"].map((field) => (
                          <label key={field}>
                            <span>{labelize(field)}</span>
                            <select value={selection[field] || ""} onChange={(event) => updateDailyNoteQuickSelectionDetail(selection.label, field, event.target.value)}>
                              <option value="">Select</option>
                              {(field === "frequency" ? dailyNoteFrequencyOptions : field === "magnitude" ? dailyNoteMagnitudeOptions : dailyNoteTrendOptions).map((option) => (
                                <option key={option} value={option}>{option}</option>
                              ))}
                            </select>
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                ) : null}
              </section>
              <section className="assessment-drawer-section">
                <h3>Notes</h3>
                <CareRichTextEditor height={76} onChange={setDailyNoteRichText} value={dailyNoteRichText} />
              </section>
              <section className="assessment-drawer-section">
                <h3>Intervention Action</h3>
                <CareRichTextEditor height={76} onChange={setDailyNoteInterventionAction} value={dailyNoteInterventionAction} />
              </section>
              <section className="assessment-drawer-section">
                <h3>Attach Photo</h3>
                <div className="privacy-consent-badge"><i className="ti ti-shield-lock" /><span>Avoid photos that compromise resident privacy. Seek consent when possible.</span></div>
                <label className="assessment-upload assessment-upload-compact">
                  <input accept="image/*,.heic,.heif" className="assessment-file-input" multiple name="photos" onChange={addDailyNotePhotos} type="file" />
                  <i className="ti ti-photo-plus" />
                  <span>Choose photos</span>
                </label>
                {dailyNotePhotos.length ? (
                  <div className="assessment-uploaded-doc-list">
                    {dailyNotePhotos.map((photo, index) => (
                      <div className="assessment-uploaded-doc-row" key={`${photo.fileName}-${index}`}>
                        <span className="assessment-attachment-thumb photo">{photo.previewUrl ? <img alt="" src={photo.previewUrl} /> : <i className="ti ti-photo" />}</span>
                        <div><strong>{photo.fileName}</strong><small>{photo.description}</small></div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </section>
              <div className="assessment-drawer-actions">
                <button className="btn secondary" type="button" disabled={dailyNoteSaving} onClick={() => setDailyNoteDrawerOpen(false)}>Cancel</button>
                <button className="btn primary" type="submit" disabled={dailyNoteSaving}>{dailyNoteSaving ? "Saving..." : "Save Daily Note"}</button>
              </div>
            </form>
          </Drawer>
          <Drawer
            title="Daily Note Details"
            placement="right"
            open={Boolean(selectedDailyNote)}
            onClose={() => setSelectedDailyNote(null)}
            width={560}
            className="resident-action-drawer daily-note-drawer"
          >
            {selectedDailyNote ? (
              <div className="resident-drawer-content">
                <section className="assessment-drawer-section">
                  <h3>Note Details</h3>
                  <div className="assessment-detail-grid">
                    <div className="wide"><span>Title</span><strong>{recordTitle(selectedDailyNote)}</strong></div>
                    <div><span>Category</span><strong>{selectedDailyNote.category || "Not set"}</strong></div>
                    <div><span>Note date</span><strong>{formatDateTime(selectedDailyNote.noteDate || selectedDailyNote.date)}</strong></div>
                    <div className="wide"><span>Time range</span><strong>{noteTimeRange(selectedDailyNote)}</strong></div>
                    <div className="wide audit-pair"><span>Captured</span><strong>{selectedDailyNote.addedBy || "Care giver"} at {formatDateTime(selectedDailyNote.date || selectedDailyNote.dateAdded)}</strong></div>
                    <div className="wide audit-pair"><span>Updated</span><strong>{selectedDailyNote.updatedBy || selectedDailyNote.addedBy || "Care giver"} at {formatDateTime(selectedDailyNote.updatedAt || selectedDailyNote.date || selectedDailyNote.dateAdded)}</strong></div>
                  </div>
                </section>
                {selectedDailyNote.quickSelections?.length ? (
                  <section className="assessment-drawer-section">
                    <h3>Quick Selections</h3>
                    <div className="quick-note-selected-list">
                      {normalizeDailyNoteQuickSelections(selectedDailyNote.quickSelections).map((selection) => (
                        <article className="quick-note-selected-card" key={selection.label}>
                          <strong>{selection.label}</strong>
                          <dl>
                            <div><dt>Frequency</dt><dd>{selection.frequency || "N/A"}</dd></div>
                            <div><dt>Magnitude</dt><dd>{selection.magnitude || "N/A"}</dd></div>
                            <div><dt>Trend</dt><dd>{selection.trend || "N/A"}</dd></div>
                          </dl>
                        </article>
                      ))}
                    </div>
                  </section>
                ) : null}
                <section className="assessment-drawer-section">
                  <h3>Notes</h3>
                  <div className="assessment-record-notes" dangerouslySetInnerHTML={{ __html: selectedDailyNote.notes || `<p>${selectedDailyNote.summary || ""}</p>` }} />
                </section>
                {selectedDailyNote.interventionAction || selectedDailyNote.intervention || selectedDailyNote.actionTaken ? (
                  <section className="assessment-drawer-section">
                    <h3>Intervention Action</h3>
                    <div className="assessment-record-notes" dangerouslySetInnerHTML={{ __html: selectedDailyNote.interventionAction || selectedDailyNote.intervention || selectedDailyNote.actionTaken }} />
                  </section>
                ) : null}
                <section className="assessment-drawer-section">
                  <h3>Attached Photos</h3>
                  {notePhotos(selectedDailyNote).length ? (
                    <div className="assessment-uploaded-doc-list">
                      {notePhotos(selectedDailyNote).map((photo, index) => {
                        const photoUrl = notePhotoUrl(photo);
                        const photoName = notePhotoName(photo, index);
                        return (
                          <div className="assessment-uploaded-doc-row" key={`${photoName}-${index}`}>
                            <span className="assessment-attachment-thumb photo">{photoUrl ? <img alt="" src={photoUrl} /> : <i className="ti ti-photo" />}</span>
                            <div><strong>{photoName}</strong><small>{photo.description || photoName}</small></div>
                            {photoUrl ? <a className="preview" href={photoUrl} target="_blank" rel="noreferrer" aria-label={`Preview ${photoName}`}><i className="ti ti-eye" /></a> : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : <p className="probation-empty">No photos attached to this note.</p>}
                </section>
              </div>
            ) : null}
          </Drawer>
        </>
      ) : null}
    </Panel>
  );
}

function OnboardingDataTab({ title, intro, sections = [], error = "", onEdit = null, onAction = null }) {
  const visibleSections = sections.filter((section) => section.rows?.length);
  return (
    <Panel title={title}>
      <div className="resident-onboarding-data-head">
        <p>{intro}</p>
        {onEdit ? (
          <button className="mini-btn" type="button" onClick={onEdit}>
            <i className="ti ti-pencil" /> Edit details
          </button>
        ) : null}
      </div>
      {error ? <p className="form-error">{error}</p> : null}
      {visibleSections.length ? (
        <div className="resident-onboarding-data-stack">
          {visibleSections.map((section) => (
            <article className="resident-onboarding-data-card" key={section.title}>
              <div className="resident-onboarding-data-card-head">
                <span><i className={`ti ${section.icon || "ti-clipboard-list"}`} /></span>
                <strong>{section.title}</strong>
              </div>
              <dl>
                {section.rows.map(([label, value, action]) => {
                  const renderedValue = onboardingValue(value);
                  return (
                    <div className={hasHtml(renderedValue) ? "is-rich" : ""} key={label}>
                      <dt>
                        {action?.type === "staff" ? (
                          <button className="resident-staff-link resident-staff-row-link" type="button" onClick={() => onAction?.(action)}>
                            {label}
                          </button>
                        ) : label}
                      </dt>
                      <dd className={renderedValue ? "" : "is-empty"}>
                        <span className="resident-onboarding-row-value">
                          {action?.type === "staff" && !renderedValue ? null : renderedValue && hasHtml(renderedValue) ? (
                            <span dangerouslySetInnerHTML={{ __html: renderedValue }} />
                          ) : (
                            renderedValue || "Not added"
                          )}
                        </span>
                        {action?.type === "document" ? (
                          <span className="resident-onboarding-doc-actions">
                            {action.viewUrl ? (
                              <a
                                className="resident-doc-icon"
                                href={action.viewUrl}
                                target="_blank"
                                rel="noreferrer"
                                title={action.isImage ? "View image" : "Preview document"}
                              >
                                <i className={`ti ${action.isImage ? "ti-photo" : "ti-eye"}`} />
                              </a>
                            ) : null}
                            {action.downloadUrl ? (
                              <a
                                className="resident-doc-icon"
                                download={action.fileName || undefined}
                                href={action.downloadUrl}
                                target="_blank"
                                rel="noreferrer"
                                title="Download document"
                              >
                                <i className="ti ti-download" />
                              </a>
                            ) : null}
                            {!action.viewUrl && !action.downloadUrl ? (
                              <span className="resident-doc-icon is-disabled" title="File link unavailable">
                                <i className="ti ti-file-off" />
                              </span>
                            ) : null}
                          </span>
                        ) : null}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </article>
          ))}
        </div>
      ) : (
        <div className="resident-inline-empty">No onboarding data has been captured for this step yet.</div>
      )}
    </Panel>
  );
}

const pendingResidentCoreLoads = new Map();

const loadResidentCore = (residentId, workspaceId) => {
  const key = `${residentId}:${workspaceId || ""}`;
  const pending = pendingResidentCoreLoads.get(key);
  if (pending) return pending;

  const workspaceParams = compactObject({ workspaceId, wid: workspaceId });
  const request = Promise.allSettled([
    careService.residents.get(residentId),
    careService.careStaffAssignments.list({
      ...workspaceParams,
      residentId,
      date: new Date().toISOString().slice(0, 10),
    }),
    careService.residents.getOnboardingPreview(residentId),
    careService.assessmentProcesses.list({ residentId }),
  ]).finally(() => {
    if (pendingResidentCoreLoads.get(key) === request) pendingResidentCoreLoads.delete(key);
  });
  pendingResidentCoreLoads.set(key, request);
  return request;
};

export default function ResidentDetailPage() {
  const { residentId, id: legacyResidentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedWorkspaceId } = useWorkspace();
  const id = residentId || legacyResidentId;
  const [activeDrawer, setActiveDrawer] = useState(null);
  const [activeTab, setActiveTab] = useState(() => window.location.hash.replace("#", "") || "overview");
  const [resident, setResident] = useState(null);
  const [residentLoading, setResidentLoading] = useState(true);
  const [residentError, setResidentError] = useState("");
  const [assignedStaff, setAssignedStaff] = useState([]);
  const [onboardingPreview, setOnboardingPreview] = useState(null);
  const [onboardingPreviewError, setOnboardingPreviewError] = useState("");
  const [residentAssessmentProcesses, setResidentAssessmentProcesses] = useState([]);
  const [residentAssessmentsError, setResidentAssessmentsError] = useState("");
  const [residentDailyNotes, setResidentDailyNotes] = useState([]);
  const [assessmentDailyNotes, setAssessmentDailyNotes] = useState([]);
  const [residentDocuments, setResidentDocuments] = useState([]);
  const [residentContacts, setResidentContacts] = useState([]);
  const [residentNegotiatedCarePlans, setResidentNegotiatedCarePlans] = useState([]);
  const [residentAssessmentRecords, setResidentAssessmentRecords] = useState([]);
  const [residentClockRecords, setResidentClockRecords] = useState([]);
  const [currentResidentClockRecord, setCurrentResidentClockRecord] = useState(null);
  const [residentClockError, setResidentClockError] = useState("");
  const loadedRelatedDataRef = useRef(new Set());
  const [caregiverSearch, setCaregiverSearch] = useState("");
  const [caregiverOptions, setCaregiverOptions] = useState([]);
  const [caregiverLoading, setCaregiverLoading] = useState(false);
  const [residencyAssignedStaffDraft, setResidencyAssignedStaffDraft] = useState([]);
  const [residencySaving, setResidencySaving] = useState(false);
  const [residentRelatedRecordsError, setResidentRelatedRecordsError] = useState("");
  const [startingAssessment, setStartingAssessment] = useState(false);
  const capturedByUserId = firstText(user?.id, user?.userId, user?.uid);
  const capturedByName = personDisplayName(user) || "Care giver";
  const residentName = fullNameFor(resident || {});
  const residentCareLevel = resident?.careLevelLabel || labelize(resident?.careLevel || "");
  const residentNcpStatus = resident?.ncpStatusLabel || labelize(resident?.ncpStatus || resident?.carePlanStatus || "");
  const residentInitials = resident?.initials || initialsFor(residentName);
  const residentStatus = labelize(resident?.status || resident?.ncpStatus || resident?.carePlanStatus || "In Review");
  const residentStatusKey = normalizeStatusKey(resident?.status);
  const residentLocation = locationFor(resident || {});
  const residentContact = contactFor(resident || {});
  const residentAge = resident?.age || ageFromBirthDate(resident?.birthDate || resident?.dateOfBirth);
  const residentRoom = resident?.roomNumber || resident?.room || "";
  const residentFloor = firstText(resident?.floorNumber, resident?.floor, residentLocation.floorNumber, residentLocation.floor);
  const residentBed = firstText(resident?.bedNumber, resident?.bed, residentLocation.bedNumber, residentLocation.bed);
  const residentUnit = firstText(resident?.unit, resident?.wing, resident?.neighborhood, residentLocation.unit, residentLocation.wing);
  const residentFacility = firstText(resident?.facilityName, resident?.residenceName, resident?.homeName, residentLocation.facilityName);
  const residentNextReview = compactDate(resident?.nextReviewDate || resident?.nextReview);
  const residentAdmissionDate = compactDate(resident?.moveInDate || resident?.expectedAdmissionDate || resident?.admissionDate || resident?.movedInDate);
  const residentAddress = addressSummaryFor(resident || {});
  const residentPrimaryContactName = resident?.primaryContactName || resident?.emergencyContactName || residentContact.contactPersonName || "";
  const residentPrimaryContactRelationship = resident?.primaryContactRelationship || resident?.emergencyContactRelationship || residentContact.contactPersonTitle || "";
  const residentPrimaryContactPhone = primaryPhoneFor(resident || {});
  const residentPrimaryContactEmail = primaryEmailFor(resident || {});
  const activeResidentAssessmentProcess = pickActiveAssessmentProcess(residentAssessmentProcesses);
  const residentFlowSource =
    activeResidentAssessmentProcess?.patientType ||
    resident?.flowType ||
    resident?.residentFlowType ||
    resident?.patientType ||
    "new-resident";
  const residentFlow = String(residentFlowSource).toUpperCase() === "NEW" ? "new-resident" : String(residentFlowSource).toUpperCase() === "RESIDENT" ? "existing-resident" : residentFlowSource;
  const residentAssessmentType = residentFlow === "new-resident" ? "NEW_RESIDENT_ASSESSMENT" : "ANNUAL_ASSESSMENT";
  const residentAssessmentLabel = residentAssessmentType === "NEW_RESIDENT_ASSESSMENT" ? "Start new resident assessment" : "Start annual assessment";
  const residentAssessmentDisplayType = residentAssessmentType === "NEW_RESIDENT_ASSESSMENT" ? "Initial Assessment / Onboarding Assessment" : "Annual Assessment";
  const residentAssessmentStage = residentAssessmentType === "NEW_RESIDENT_ASSESSMENT" ? "STATE_INITIAL_ASSESSMENT" : "YEARLY_STATE_ASSESSMENT";
  const residentAssessmentStatus = normalizeStatusKey(
    activeResidentAssessmentProcess?.stage ||
    activeResidentAssessmentProcess?.workflowStatus ||
    activeResidentAssessmentProcess?.status ||
    resident?.assessmentStatus ||
    resident?.assessmentProcessStatus ||
    resident?.currentAssessmentStatus ||
    resident?.ncpStatus ||
    resident?.carePlanStatus
  );
  const activeAssessmentProcessReference =
    activeResidentAssessmentProcess?.id ||
    activeResidentAssessmentProcess?.assessmentProcessId ||
    activeResidentAssessmentProcess?.processId ||
    resident?.activeAssessmentProcessId ||
    resident?.currentAssessmentProcessId ||
    resident?.openAssessmentProcessId;
  const activeAssessmentRecordType = assessmentProcessRecordTypeFor(activeResidentAssessmentProcess || {}, resident || {});
  const activeAssessmentStatusKeys = ["OPEN", "ACTIVE", "IN_PROGRESS", "STATE_INITIAL_ASSESSMENT", "YEARLY_STATE_ASSESSMENT", "MEET_AND_GREET", "PROVIDER_REVIEW", "THIRTY_DAY_PROBATION", "PRELIMINARY_CARE_PLAN", "NEGOTIATED_CARE_PLAN", "PAYMENT_NEGOTIATION", "FINAL_ASSESSMENT_UPDATE", "STATE_CONFIRMATION"];
  const hasActiveResidentAssessmentProcess =
    Boolean(activeResidentAssessmentProcess || activeAssessmentProcessReference) ||
    activeAssessmentStatusKeys.includes(residentAssessmentStatus);
  const canStartResidentAssessment = !residentLoading && residentAssessmentStatus === "NOT_STARTED" && !hasActiveResidentAssessmentProcess;
  const canCreateAnnualAssessment = !residentLoading && residentStatusKey === "ACTIVE" && !hasActiveResidentAssessmentProcess;
  const residentFlowStages = carePlanFlowStages.filter((stage) => stage.appliesTo.includes(residentFlow));
  const actualFlowStageKey =
    activeResidentAssessmentProcess?.stage ||
    activeResidentAssessmentProcess?.workflowStage ||
    activeResidentAssessmentProcess?.currentStage ||
    (activeResidentAssessmentProcess ? residentAssessmentStage : "") ||
    resident?.assessmentStage ||
    resident?.assessmentProcessStage ||
    resident?.currentAssessmentStage ||
    resident?.ncpStage ||
    resident?.ncpStatus ||
    resident?.carePlanStatus ||
    "";
  const actualFlowStageIndex = stageIndexFor(residentFlowStages, actualFlowStageKey);
  const residentFlowStagesWithStatus = residentFlowStages.map((stage, index) => {
    const status = actualFlowStageIndex < 0 ? "Pending" : index < actualFlowStageIndex ? "Complete" : index === actualFlowStageIndex ? "Current" : "Pending";
    return {
      ...stage,
      displayLabel: residentFlow === "existing-resident" && stage.existingLabel ? stage.existingLabel : stage.label,
      displayText: residentFlow === "existing-resident" && stage.existingText ? stage.existingText : stage.text,
      status,
    };
  });
  const residentAssignedStaffRecords = asList(resident?.assignedStaff || resident?.residencyDetails?.assignedStaff);
  const currentAssignedStaff = assignedStaff.length ? assignedStaff : residentAssignedStaffRecords;
  const residentAssignedStaffSummary = currentAssignedStaff.map(staffAssignmentLabel).filter(Boolean).join(", ");
  const residentResidencySummary = [
    residentFacility,
    residentUnit ? `Unit ${residentUnit}` : "",
    residentFloor ? `Floor ${residentFloor}` : "",
    residentRoom ? `Room ${residentRoom}` : "",
    residentBed ? `Bed ${residentBed}` : "",
  ].filter(Boolean).join(" | ");
  const activeStaff = currentAssignedStaff.find((staff) => String(staffDrawerId(staff)) === String(activeDrawer));
  const submitResidentAssessmentStart = async () => {
    if (!id || startingAssessment) return;
    setStartingAssessment(true);
    try {
      const payload = {
        assessmentType: residentAssessmentType,
        patientType: residentAssessmentType === "NEW_RESIDENT_ASSESSMENT" ? "NEW" : "RESIDENT",
        residentId: id,
        residentName,
        startDate: new Date().toISOString().slice(0, 10),
        status: "OPEN",
        stage: residentAssessmentStage,
      };
      const created = await careService.assessmentProcesses.create(payload);
      const createdId = created?.id || created?.assessmentProcessId || created?.processId;
      setResidentAssessmentProcesses((processes) => [created || { ...payload, id: createdId }, ...processes]);
      message.success(`${residentAssessmentDisplayType} started for ${residentName}.`);
      if (createdId) navigate(`/assessments/${createdId}/records/initial-assessment`);
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to start assessment process.");
    } finally {
      setStartingAssessment(false);
    }
  };
  const submitAnnualAssessmentProcessStart = async () => {
    if (!id || startingAssessment) return;
    setStartingAssessment(true);
    try {
      const payload = {
        assessmentType: "ANNUAL_ASSESSMENT",
        patientType: "RESIDENT",
        residentId: id,
        residentName,
        startDate: new Date().toISOString().slice(0, 10),
        status: "OPEN",
        stage: "YEARLY_STATE_ASSESSMENT",
      };
      const created = await careService.assessmentProcesses.create(payload);
      const createdId = created?.id || created?.assessmentProcessId || created?.processId;
      setResidentAssessmentProcesses((processes) => [created || { ...payload, id: createdId }, ...processes]);
      message.success(`Annual assessment process started for ${residentName}.`);
      if (createdId) navigate(`/assessments/${createdId}/new`);
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to create annual assessment process.");
    } finally {
      setStartingAssessment(false);
    }
  };
  const startResidentAssessment = () => {
    if (!id || startingAssessment) return;
    Modal.confirm({
      title: `Start ${residentAssessmentDisplayType}?`,
      content: `Would you like to confirm initiating the ${residentAssessmentDisplayType} for ${residentName}? Once confirmed, you will move to the State Initial Assessment step.`,
      okText: "Confirm and start",
      cancelText: "Cancel",
      centered: true,
      onOk: submitResidentAssessmentStart,
    });
  };
  const startAnnualAssessmentProcess = () => {
    if (!id || startingAssessment) return;
    Modal.confirm({
      title: "Create annual assessment process?",
      content: `Would you like to confirm initiating the Annual Assessment process for ${residentName}? Once confirmed, the process will move to the Yearly State Assessment step.`,
      okText: "Confirm and start",
      cancelText: "Cancel",
      centered: true,
      onOk: submitAnnualAssessmentProcessStart,
    });
  };
  const residentPersonaDrawer = {
    title: "Resident Persona",
    intro: "Resident bio data, contact information, next of kin details, and care profile from the resident record.",
    sections: [
      {
        title: "Bio Data",
        rows: [
          ["Full name", displayValue(residentName)],
          ["Preferred name", displayValue(resident?.preferredName)],
          ["Date of birth", displayValue(compactDate(resident?.birthDate || resident?.dateOfBirth))],
          ["Age", displayValue(residentAge)],
          ["Gender", displayValue(labelize(resident?.gender || resident?.sex))],
          ["Primary language", displayValue(resident?.primaryLanguage)],
          ["Status", displayValue(residentStatus)],
          ["Admission date", displayValue(residentAdmissionDate)],
          ["Discharge date", displayValue(compactDate(resident?.dischargeDate))],
        ],
      },
      {
        title: "Residency Details",
        rows: [
          ["Facility / residence", displayValue(residentFacility)],
          ["Unit / wing", displayValue(residentUnit)],
          ["Floor", displayValue(residentFloor)],
          ["Room", displayValue(residentRoom)],
          ["Bed", displayValue(residentBed)],
          ["Assigned staff", displayValue(residentAssignedStaffSummary)],
          ["Staff assignment count", currentAssignedStaff.length ? String(currentAssignedStaff.length) : "Not set"],
          ["Residency notes", displayValue(resident?.residencyNotes || resident?.placementNotes)],
        ],
      },
      {
        title: "Contact Information",
        rows: [
          ["Phone", displayValue(residentPrimaryContactPhone || residentContact.primaryPhone)],
          ["Secondary phone", displayValue(residentContact.secondaryPhone)],
          ["Email", displayValue(residentPrimaryContactEmail || residentContact.primaryEmail)],
          ["Secondary email", displayValue(residentContact.secondaryEmail)],
          ["Address", displayValue(residentAddress)],
          ["City", displayValue(residentLocation.city)],
          ["County", displayValue(residentLocation.county)],
          ["State", displayValue(residentLocation.state)],
          ["Zip Code", displayValue(residentLocation.zipCode || residentLocation.zip)],
        ],
      },
      {
        title: "Primary Contact / Next of Kin",
        rows: [
          ["Name", displayValue(residentPrimaryContactName)],
          ["Relationship", displayValue(residentPrimaryContactRelationship)],
          ["Phone", displayValue(residentPrimaryContactPhone)],
          ["Email", displayValue(residentPrimaryContactEmail)],
          ["PoA / guardian notes", displayValue(resident?.poaGuardianNotes)],
        ],
      },
      {
        title: "Care Profile",
        rows: [
          ["Care level", displayValue(residentCareLevel)],
          ["NCP status", displayValue(residentNcpStatus)],
          ["Next review", displayValue(residentNextReview)],
          ["Primary provider", displayValue(resident?.primaryProvider)],
          ["Pharmacy", displayValue(resident?.pharmacy)],
          ["Diagnosis", displayValue(resident?.diagnosis)],
          ["Medication notes", displayValue(resident?.medicationNotes)],
          ["Care needed", displayValue(resident?.careNeeded)],
          ["Behavior and safety notes", displayValue(resident?.behaviorSafetyNotes)],
          ["General notes", displayValue(resident?.notes)],
        ],
      },
    ],
  };
  const residentPrimaryContactSection = residentPrimaryContactName ? careTeamSectionFor({
    name: residentPrimaryContactName,
    relationship: residentPrimaryContactRelationship || "Primary contact",
    phone: residentPrimaryContactPhone,
    email: residentPrimaryContactEmail,
    createdAt: resident.createdAt || resident.updatedAt,
    notes: resident?.poaGuardianNotes,
    primaryContact: true,
  }, 0, "Resident Primary Contact") : null;
  const staffCareTeamSections = sortedRecordRows(currentAssignedStaff.map((member) => ({ ...member, dateAdded: recordDate(member) })))
    .map((staffRecord, index) => careTeamSectionFor(staffRecord, index, "Assigned Staff Today"));
  const residentContactSections = sortedRecordRows(residentContacts.map((contact) => ({ ...contact, dateAdded: recordDate(contact) })))
    .map((contactRecord, index) => careTeamSectionFor(contactRecord, index, "Resident Contact"));
  const careTeamDrawerSections = [
    ...staffCareTeamSections,
    ...(residentPrimaryContactSection ? [residentPrimaryContactSection] : []),
    ...residentContactSections,
  ];
  const snapshot = [
    ["Admission", residentAdmissionDate || "Not set"],
    ["Primary contact", residentPrimaryContactName || "Not set"],
    ["Relationship", residentPrimaryContactRelationship || "Not set"],
    ["Residency", residentResidencySummary || "Not set"],
    ["Assigned staff", currentAssignedStaff.length ? String(currentAssignedStaff.length) : "Not set"],
    ["Care level", residentCareLevel || "Not set"],
    ["NCP status", residentNcpStatus || "Not set"],
    ["Diagnosis", resident?.diagnosis || "Not set"],
    ["Medication support", resident?.medicationNotes || "Not set"],
  ];
  const residentActionSections = residentSections.map(([key, icon, title, text]) => {
    if (key === "notes") {
      const noteCount = assessmentDailyNotes.length || residentDailyNotes.length;
      return [key, icon, title, resident?.lastNoteSummary || text, noteCount ? `${noteCount} note${noteCount === 1 ? "" : "s"}` : "No notes", resident?.lastNoteAt];
    }
    if (key === "carePlan") {
      return [key, icon, title, resident?.careNeeded || text, residentNegotiatedCarePlans.length ? `${residentNegotiatedCarePlans.length} plan${residentNegotiatedCarePlans.length === 1 ? "" : "s"}` : residentNcpStatus || "No plans", resident?.updatedAt];
    }
    if (key === "documents") {
      const documentCount = residentDocuments.length + (resident?.stateAssessmentFileName ? 1 : 0);
      return [key, icon, title, resident?.stateAssessmentFileName ? `State assessment: ${resident.stateAssessmentFileName}` : text, documentCount ? `${documentCount} file${documentCount === 1 ? "" : "s"}` : "No files", resident?.updatedAt];
    }
    if (key === "alerts") {
      return [key, icon, title, text, `${Number(resident?.openAlertCount || 0)} open`, resident?.updatedAt];
    }
    if (key === "assessments") {
      return [key, icon, title, resident?.intakeSource ? `Intake source: ${resident.intakeSource}` : text, residentAssessmentProcesses.length ? `${residentAssessmentProcesses.length} assessment${residentAssessmentProcesses.length === 1 ? "" : "s"}` : "No assessments", resident?.updatedAt];
    }
    if (key === "team") {
      const teamCount = residentContacts.length + currentAssignedStaff.length;
      return [key, icon, title, resident?.careTeamSummary || text, teamCount ? `${teamCount} contact${teamCount === 1 ? "" : "s"}` : "No care team", resident?.updatedAt];
    }
    return [key, icon, title, text, "Not set", resident?.updatedAt];
  });
  const onboardingRoot = onboardingPreview?.previewPayload || onboardingPreview?.payload || onboardingPreview || {};
  const onboardingStages =
    onboardingRoot.onboardingStages ||
    onboardingRoot.previewPayload?.onboardingStages ||
    onboardingRoot.payload?.onboardingStages ||
    {};
  const bioStage = onboardingStages.bio || onboardingRoot.bio || {};
  const contactsStage = onboardingStages.contacts || onboardingRoot.contacts || {};
  const careHistoryStage = onboardingStages.careHistory || onboardingRoot.careHistory || {};
  const careSnapshotStage = onboardingStages.careSnapshot || onboardingStages.care || onboardingRoot.careSnapshot || onboardingRoot.care || {};
  const documentsStage = onboardingStages.documents || onboardingRoot.documents || {};
  const onboardingBio = bioStage.resident || bioStage.payload?.resident || bioStage.payload || bioStage || {};
  const onboardingBioSourceForm = onboardingBio.sourceForm || {};
  const onboardingBioFormData = onboardingBioSourceForm.formData || {};
  const onboardingResidency = onboardingBio.residencyDetails || onboardingBio.residency || onboardingBio.placement || onboardingBio.housing || {};
  const onboardingResidencyStaff = asList(onboardingResidency.assignedStaff || onboardingBio.assignedStaff || onboardingBioFormData.assignedStaff);
  const onboardingAssignedStaffSummary = (onboardingResidencyStaff.length ? onboardingResidencyStaff : currentAssignedStaff)
    .map((staff) => typeof staff === "string" ? staff : staffAssignmentLabel(staff))
    .filter(Boolean)
    .join(", ");
  const onboardingFloor = firstText(onboardingResidency.floorNumber, onboardingResidency.floor, onboardingBioFormData.floorNumber, onboardingBioFormData.floor, residentFloor);
  const onboardingRoom = firstText(onboardingResidency.roomNumber, onboardingResidency.room, onboardingBioFormData.roomNumber, onboardingBioFormData.room, residentRoom);
  const onboardingBed = firstText(onboardingResidency.bedNumber, onboardingResidency.bed, onboardingBioFormData.bedNumber, onboardingBioFormData.bed, residentBed);
  const onboardingUnit = firstText(onboardingResidency.unit, onboardingResidency.wing, onboardingBioFormData.unit, onboardingBioFormData.wing, residentUnit);
  const onboardingFacility = firstText(onboardingResidency.facilityName, onboardingResidency.residenceName, onboardingBioFormData.facilityName, onboardingBioFormData.residenceName, residentFacility);
  const residentCoverage = {
    codeStatus: resident?.codeStatus,
    advancedDirectives: resident?.advancedDirectives,
    medicareNumber: resident?.medicareNumber,
    medicaidNumber: resident?.medicalRecordNumber,
    supplementaryInsurances: resident?.supplementaryInsurances,
  };
  const residentBioAddress = {
    addressLine1: residentLocation.addressLine1 || residentLocation.address,
    addressLine2: residentLocation.addressLine2,
    city: residentLocation.city,
    county: residentLocation.county,
    state: residentLocation.state,
    country: residentLocation.country,
    zipCode: residentLocation.zipCode || residentLocation.zip,
  };
  const onboardingContacts = contactsStage.contacts || contactsStage.payload?.contacts || onboardingBio.contacts || [];
  const onboardingCareHistory = careHistoryStage.careHistory || careHistoryStage.payload?.careHistory || onboardingBio.careHistory || {};
  const onboardingCareSnapshot = careSnapshotStage.initialCareSnapshot || careSnapshotStage.payload?.initialCareSnapshot || onboardingBio.initialCareSnapshot || {};
  const onboardingDocuments = documentsStage.documents || documentsStage.payload?.documents || onboardingBio.documents || {};
  const bioPersonal = onboardingBio.personalInformation || {};
  const bioCoverage = onboardingBio.healthAndCoverage || residentCoverage;
  const onboardingBioSections = [
    {
      title: "Personal Information",
      icon: "ti-user-circle",
      rows: [
        ["Full name", [bioPersonal.firstName || onboardingBioFormData.firstName || resident?.firstName, bioPersonal.middleName || onboardingBioFormData.middleName || resident?.middleName, bioPersonal.lastName || onboardingBioFormData.lastName || resident?.lastName].filter(Boolean).join(" ") || residentName],
        ["Preferred name", bioPersonal.preferredName || onboardingBioFormData.preferredName || resident?.preferredName],
        ["Birth date", compactDate(bioPersonal.birthDate || onboardingBioFormData.dateOfBirth || resident?.birthDate || resident?.dateOfBirth)],
        ["Sex", labelize(bioPersonal.sex || onboardingBioFormData.sex || resident?.gender)],
        ["Social Security Number", bioPersonal.socialSecurityNumber || onboardingBioFormData.socialSecurityNumber],
        ["Primary language", bioPersonal.primaryLanguage || onboardingBioFormData.primaryLanguage || resident?.primaryLanguage],
      ],
    },
    {
      title: "Address",
      icon: "ti-map-pin",
      rows: [
        ["Address", addressSummary(onboardingBio.address) || addressSummary(residentBioAddress) || residentAddress],
        ["City", onboardingBio.address?.city || onboardingBioFormData.city || residentLocation.city],
        ["County", onboardingBio.address?.county || onboardingBioFormData.county || residentLocation.county],
        ["State", onboardingBio.address?.state || onboardingBioFormData.state || residentLocation.state],
        ["Country", onboardingBio.address?.country || onboardingBioFormData.country || residentLocation.country],
        ["Zip Code", onboardingBio.address?.zipCode || onboardingBio.address?.zip || onboardingBioFormData.zip || residentLocation.zipCode],
      ],
    },
    {
      title: "Residency Details",
      icon: "ti-building-community",
      rows: [
        ["Facility / residence", onboardingFacility],
        ["Unit / wing", onboardingUnit],
        ["Floor", onboardingFloor],
        ["Room", onboardingRoom],
        ["Bed", onboardingBed],
        ["Assigned staff", onboardingAssignedStaffSummary],
        ["Staff assignment count", (onboardingResidencyStaff.length || currentAssignedStaff.length) ? String(onboardingResidencyStaff.length || currentAssignedStaff.length) : ""],
        ["Residency notes", onboardingResidency.notes || onboardingBioFormData.residencyNotes || resident?.residencyNotes || resident?.placementNotes],
      ],
    },
    {
      title: "Coverage and Notes",
      icon: "ti-shield-check",
      rows: [
        ["Code status", bioCoverage.codeStatus || onboardingBioFormData.codeStatus],
        ["Advanced directives", bioCoverage.advancedDirectives ?? onboardingBioFormData.advancedDirectives],
        ["Medicare Number", bioCoverage.medicareNumber || onboardingBioFormData.medicareNumber],
        ["Medicaid Number", bioCoverage.medicaidNumber || onboardingBioFormData.medicaidNumber],
        ["Supplementary insurances", bioCoverage.supplementaryInsurances || onboardingBioSourceForm.otherInsurances],
        ["Background notes", onboardingBio.backgroundNotes || onboardingBio.notes || onboardingBioFormData.bioNotes || resident?.notes],
      ],
    },
  ];
  const residencyDetailSections = [
    {
      title: "Residency Details",
      icon: "ti-building-community",
      rows: [
        ["Facility / residence", residentFacility],
        ["Unit / wing", residentUnit],
        ["Floor", residentFloor],
        ["Room", residentRoom],
        ["Bed", residentBed],
        ["Admission date", residentAdmissionDate],
        ["Discharge date", compactDate(resident?.dischargeDate)],
        ["Residency notes", resident?.residencyNotes || resident?.placementNotes],
      ],
    },
    {
      title: "Assigned Staff",
      icon: "ti-users-group",
      rows: currentAssignedStaff.length
        ? currentAssignedStaff.map((staff, index) => [
          careTeamMemberName(staff) || `Staff ${index + 1}`,
          [
            careTeamEmail(staff),
            careTeamPhone(staff),
            firstText(staff.assignment, staff.description),
          ].filter(Boolean).join(" | "),
          { type: "staff", staffId: staffDrawerId(staff) },
        ])
        : [["Assigned staff", "No staff assignment found for today."]],
    },
  ];
  const fallbackContacts = residentPrimaryContactName ? [{
    relationship: residentPrimaryContactRelationship || "Primary contact",
    firstName: residentPrimaryContactName,
    email: residentPrimaryContactEmail,
    phones: { preferred: residentPrimaryContactPhone },
    primaryContact: true,
  }] : [];
  const onboardingContactSections = (onboardingContacts.length ? onboardingContacts : fallbackContacts).map((contact, index) => ({
    title: `${contact.relationship || contact.contactType || "Contact"} ${index + 1}`,
    icon: "ti-address-book",
    rows: [
      ["Name", [contact.firstName, contact.middleName, contact.lastName].filter(Boolean).join(" ") || contact.organizationName],
      ["Relationship", contact.relationship],
      ["Contact type", labelize(contact.contactType)],
      ["Email", contact.email],
      ["Phones", phonesSummary(contact.phones)],
      ["Address", addressSummary(contact.address)],
      ["Primary contact", contact.primaryContact],
      ["Emergency contact", contact.emergencyContact],
      ["Decision maker", contact.decisionMaker],
      ["Next of kin", contact.nextOfKin],
      ["Notes", contact.notes],
    ],
  }));
  const onboardingCareHistorySections = [
    {
      title: "Admission Source",
      icon: "ti-building-hospital",
      rows: [
        ["Admitted at hospital", onboardingCareHistory.admittedAtHospital],
        ["Hospital", onboardingCareHistory.hospitalAdmission?.name || onboardingBioFormData.hospitalName],
        ["Hospital contact", onboardingCareHistory.hospitalAdmission?.contactName || onboardingBioFormData.hospitalContactName],
        ["Hospital phone", phonesSummary(onboardingCareHistory.hospitalAdmission?.phones) || onboardingBioFormData.hospitalPhone],
        ["Hospital email", onboardingCareHistory.hospitalAdmission?.email || onboardingBioFormData.hospitalEmail],
        ["Hospital address", addressSummary(onboardingCareHistory.hospitalAdmission?.address) || addressSummary(locationFor({ locationInformation: {
          addressLine1: onboardingBioFormData.hospitalAddress,
          city: onboardingBioFormData.hospitalCity,
          county: onboardingBioFormData.hospitalCounty,
          state: onboardingBioFormData.hospitalState,
          country: onboardingBioFormData.hospitalCountry,
          zipCode: onboardingBioFormData.hospitalZip,
        } }))],
        ["Hospital notes", onboardingCareHistory.hospitalAdmission?.notes || onboardingBioFormData.admissionDescription],
      ],
    },
    {
      title: "Previous Family Home",
      icon: "ti-home-heart",
      rows: [
        ["Coming from another family home", onboardingCareHistory.comingFromAnotherFamilyHome],
        ["Family home name", onboardingCareHistory.previousFamilyHome?.name],
        ["Contact name", onboardingCareHistory.previousFamilyHome?.contactName],
        ["Phone", phonesSummary(onboardingCareHistory.previousFamilyHome?.phones)],
        ["Email", onboardingCareHistory.previousFamilyHome?.email],
        ["Address", addressSummary(onboardingCareHistory.previousFamilyHome?.address)],
        ["Length of stay", [onboardingCareHistory.previousFamilyHome?.years ? `${onboardingCareHistory.previousFamilyHome.years} years` : "", onboardingCareHistory.previousFamilyHome?.months ? `${onboardingCareHistory.previousFamilyHome.months} months` : ""].filter(Boolean).join(", ")],
        ["Notes", onboardingCareHistory.previousFamilyHome?.notes],
      ],
    },
    {
      title: "Prior Caregiver",
      icon: "ti-user-heart",
      rows: [
        ["Had prior caregiver", onboardingCareHistory.hadPriorCaregiver],
        ["Caregiver name", [onboardingCareHistory.priorCaregiver?.firstName, onboardingCareHistory.priorCaregiver?.middleName, onboardingCareHistory.priorCaregiver?.lastName].filter(Boolean).join(" ")],
        ["Relationship", onboardingCareHistory.priorCaregiver?.relationship],
        ["Phone", phonesSummary(onboardingCareHistory.priorCaregiver?.phones)],
        ["Email", onboardingCareHistory.priorCaregiver?.email],
        ["Address", addressSummary(onboardingCareHistory.priorCaregiver?.address)],
        ["Care details", onboardingCareHistory.priorCaregiver?.notes],
      ],
    },
    {
      title: "Care History Notes",
      icon: "ti-notes",
      rows: [["Notes", onboardingCareHistory.notes]],
    },
  ];
  const onboardingCareSnapshotSections = [
    {
      title: "Initial Care Snapshot",
      icon: "ti-heart-rate-monitor",
      rows: [
        ["Condition summary", onboardingCareSnapshot.conditionSummary || onboardingBioFormData.admissionDescription],
        ["Diagnosis", onboardingCareSnapshot.diagnosis || onboardingBioFormData.diagnosis || resident?.diagnosis],
        ["Allergies", onboardingCareSnapshot.allergies || onboardingBioFormData.allergies],
        ["Medication notes", onboardingCareSnapshot.medicationNotes || onboardingBioFormData.medicationNotes || resident?.medicationNotes],
        ["Care needed", onboardingCareSnapshot.careNeeded || onboardingBioFormData.careNeeded || resident?.careNeeded],
        ["Behavior and safety notes", onboardingCareSnapshot.behaviorSafetyNotes || onboardingBioFormData.behaviorSafetyNotes || resident?.behaviorSafetyNotes],
      ],
    },
    {
      title: "Custom Questions",
      icon: "ti-question-mark",
      rows: (onboardingCareSnapshot.customQuestions || []).map((question, index) => [
        question.question || `Question ${index + 1}`,
        question.answer || question.response,
      ]),
    },
  ];
  const onboardingDocumentSections = [
    {
      title: "Uploaded Documents",
      icon: "ti-file-upload",
      rows: (onboardingDocuments.uploadedDocuments || onboardingPreview?.uploadedDocuments || (resident?.stateAssessmentFileName ? [{ name: resident.stateAssessmentFileName, fileName: resident.stateAssessmentFileName, description: "State assessment file" }] : [])).map((document, index) => {
        const fileName = document.fileName || document.name || `Document ${index + 1}`;
        const fileUrl = onboardingDocumentUrlFor(document);
        return [
          document.name || document.fileName || `Document ${index + 1}`,
          onboardingDocumentMetaFor(document),
          {
            type: "document",
            fileName,
            viewUrl: fileUrl,
            downloadUrl: document.downloadUrl || fileUrl,
            isImage: isImageOnboardingDocument(document),
          },
        ];
      }),
    },
    {
      title: "Signed Document Validation",
      icon: "ti-signature",
      rows: (onboardingDocuments.signedDocumentValidations || []).map((document) => [
        document.documentName || "Signed document",
        `Signed verified: ${document.signedVerified ? "Yes" : "No"} | File uploaded: ${document.fileUploaded ? "Yes" : "No"}${document.notes ? ` | Notes: ${document.notes}` : ""}`,
      ]),
    },
  ];
  const mappedResidentAssessments = dedupeResidentAssessments(
    residentAssessmentProcesses.map((process) => ({ ...process, __assessmentProcessSource: true })),
  )
    .sort((a, b) => residentAssessmentDateValue(b) - residentAssessmentDateValue(a))
    .map(mapResidentAssessmentRecord);
  const residentAssessmentSplit = splitCurrentAndPrevious(mappedResidentAssessments, isClosedResidentAssessment);
  const currentResidentAssessment = residentAssessmentSplit.current;
  const previousResidentAssessments = residentAssessmentSplit.previous;
  const dynamicActiveAssessmentProcessId = assessmentRedirectIdFor({
    processes: residentAssessmentProcesses,
    assessments: mappedResidentAssessments,
    preview: onboardingPreview,
    resident: resident || {},
  });
  const activeAssessmentBehavioralNotesPath = dynamicActiveAssessmentProcessId
    ? `/assessments/${dynamicActiveAssessmentProcessId}/new#behavioral-notes`
    : "";
  const hasCurrentActiveAssessment = mappedResidentAssessments.some(isActiveResidentAssessment);
  const showStartNewAssessment = !residentLoading && !hasCurrentActiveAssessment;
  const currentAssessmentDailyNotes = assessmentDailyNotes.length ? assessmentDailyNotes : residentDailyNotes;
  const mappedDailyNotes = currentAssessmentDailyNotes.map(mapDailyNoteRecord);
  const currentDailyNote = splitCurrentAndPrevious(mappedDailyNotes).current;
  const previousDailyNotes = splitCurrentAndPrevious(mappedDailyNotes).previous;
  const onboardingUploadedDocuments = asList(onboardingDocuments.uploadedDocuments || onboardingPreview?.uploadedDocuments)
    .map((document) => ({
      ...document,
      title: firstText(document.title, document.name, document.fileName, "Onboarding document"),
      summary: onboardingDocumentMetaFor(document) || firstText(document.description, "Onboarding document"),
      dateAdded: recordDate(document) || residentAdmissionDate,
      addedBy: addedByFor(document),
      status: document.confirmed ? "Confirmed" : labelize(firstText(document.storageStatus, document.status, "Uploaded")),
    }));
  const mappedDocuments = [
    ...residentDocuments.map(mapDocumentRecord),
    ...onboardingUploadedDocuments,
    ...(resident?.stateAssessmentFileName ? [mapDocumentRecord({
      title: resident.stateAssessmentFileName,
      fileName: resident.stateAssessmentFileName,
      description: "State assessment file",
      createdAt: resident.updatedAt || resident.createdAt,
      status: resident.stateAssessmentImported ? "Imported" : "Available",
    })] : []),
  ];
  const currentDocument = splitCurrentAndPrevious(mappedDocuments).current;
  const previousDocuments = splitCurrentAndPrevious(mappedDocuments).previous;
  const mappedCareTeam = [
    ...residentContacts.map(mapCareTeamRecord),
    ...currentAssignedStaff.map(mapCareTeamRecord),
    ...(residentPrimaryContactName ? [mapCareTeamRecord({
      name: residentPrimaryContactName,
      relationship: residentPrimaryContactRelationship || "Primary contact",
      phone: residentPrimaryContactPhone,
      email: residentPrimaryContactEmail,
      createdAt: resident.createdAt || resident.updatedAt,
      summary: [residentPrimaryContactPhone, residentPrimaryContactEmail].filter(Boolean).join(" | "),
    })] : []),
  ];
  const currentCareTeamMember = splitCurrentAndPrevious(mappedCareTeam).current;
  const previousCareTeamMembers = splitCurrentAndPrevious(mappedCareTeam).previous;
  const mappedPreliminaryPlans = residentAssessmentRecords
    .filter(isPreliminaryPlanRecord)
    .map(mapPreliminaryPlanRecord);
  const currentPreliminaryPlan = splitCurrentAndPrevious(mappedPreliminaryPlans).current;
  const previousPreliminaryPlans = splitCurrentAndPrevious(mappedPreliminaryPlans).previous;
  const residencyDrawerSections = [
    {
      title: "Placement Details",
      rows: [
        ["Unit / wing", residentUnit, { name: "unit" }],
        ["Floor", residentFloor, { name: "floor" }],
        ["Room", residentRoom, { name: "roomNumber" }],
        ["Bed", residentBed, { name: "bedNumber" }],
        ["Residency notes", resident?.residencyNotes || resident?.placementNotes, { name: "residencyNotes" }],
      ],
    },
    {
      title: "Admission Timeline",
      rows: [
        ["Admission date", residentAdmissionDate],
        ["Discharge date", compactDate(resident?.dischargeDate)],
      ],
    },
  ];
  const residencyStaffPicker = {
    search: caregiverSearch,
    selected: residencyAssignedStaffDraft,
    options: caregiverOptions,
    loading: caregiverLoading,
    onSearchChange: setCaregiverSearch,
    onToggle: (profile) => {
      const optionId = careProfileOptionId(profile);
      if (!optionId) return;
      setResidencyAssignedStaffDraft((current) =>
        current.some((staff) => careProfileOptionId(staff) === optionId)
          ? current.filter((staff) => careProfileOptionId(staff) !== optionId)
          : [...current, profile]
      );
    },
    onRemove: (optionId) => {
      setResidencyAssignedStaffDraft((current) => current.filter((staff) => careProfileOptionId(staff) !== optionId));
    },
  };
  const saveResidencyDetails = async (values = {}) => {
    if (!id || residencySaving) return;
    setResidencySaving(true);
    const assignedStaffPayload = residencyAssignedStaffDraft.map((staff) => ({
      careProfileId: careProfileOptionId(staff),
      caregiverUserId: firstText(staff.userProfile?.id, staff.user?.id, staff.userId, staff.userProfileId),
      caregiverName: careProfileOptionName(staff),
      caregiverEmail: careProfileOptionEmail(staff) === "No email added" ? "" : careProfileOptionEmail(staff),
    })).filter((staff) => staff.careProfileId || staff.caregiverUserId || staff.caregiverEmail);
    const residencyPayload = compactObject({
      unit: values.unit,
      wing: values.unit,
      floor: values.floor,
      floorNumber: values.floor,
      room: values.roomNumber,
      roomNumber: values.roomNumber,
      bed: values.bedNumber,
      bedNumber: values.bedNumber,
      residencyNotes: values.residencyNotes,
      placementNotes: values.residencyNotes,
      assignedStaff: assignedStaffPayload,
      locationInformation: {
        ...(residentLocation || {}),
        unit: values.unit,
        wing: values.unit,
        floor: values.floor,
        floorNumber: values.floor,
        room: values.roomNumber,
        roomNumber: values.roomNumber,
        bed: values.bedNumber,
        bedNumber: values.bedNumber,
      },
      residencyDetails: {
        unit: values.unit,
        wing: values.unit,
        floor: values.floor,
        floorNumber: values.floor,
        room: values.roomNumber,
        roomNumber: values.roomNumber,
        bed: values.bedNumber,
        bedNumber: values.bedNumber,
        notes: values.residencyNotes,
        assignedStaff: assignedStaffPayload,
      },
    });
    try {
      let savedResident;
      try {
        savedResident = await careService.residents.saveResidencyDetails(id, residencyPayload);
      } catch (err) {
        if (![404, 405].includes(err?.response?.status)) throw err;
        savedResident = await careService.residents.update(id, { ...(resident || {}), ...residencyPayload });
      }
      setResident((current) => ({ ...(current || {}), ...(savedResident && typeof savedResident === "object" ? savedResident : residencyPayload) }));
      setAssignedStaff(assignedStaffPayload);
      message.success("Residency details saved.");
      closeDrawer();
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to save residency details.");
    } finally {
      setResidencySaving(false);
    }
  };
  const dataDrawerContent = {
    residency: {
      title: "Residency Details",
      intro: "Update placement details and assigned staff. Admission and discharge dates are shown for reference only.",
      sections: residencyDrawerSections,
      staffPicker: residencyStaffPicker,
      onSave: saveResidencyDetails,
      saving: residencySaving,
    },
    contacts: {
      title: "Contacts",
      intro: "Family, guardians, PoA, emergency contacts, and care-circle contacts for this resident.",
      sections: onboardingContactSections,
    },
    careHistory: {
      title: "Care History",
      intro: "Hospital admission, prior home, prior caregiver, and care history details.",
      sections: onboardingCareHistorySections,
    },
    careSnapshot: {
      title: "Care Snapshot",
      intro: "Current care needs, diagnosis, medication, behavior, and custom care questions.",
      sections: onboardingCareSnapshotSections,
    },
    onboardingDocuments: {
      title: "Onboarding Documents",
      intro: "Uploaded files and signed-document validations captured during onboarding.",
      sections: onboardingDocumentSections,
    },
    notes: {
      title: "Daily Notes",
      intro: `Daily note records loaded from the current assessment for ${residentName}.`,
      rows: drawerRowsFromRecords(
        assessmentDailyNotes.length ? assessmentDailyNotes : residentDailyNotes,
        mapDailyNoteRecord,
        "No daily notes have been added for this resident's current assessment.",
      ),
    },
    documents: {
      title: "Files and Documents",
      intro: "Resident documents loaded from the care service and onboarding record.",
      rows: drawerRowsFromRecords(mappedDocuments, (record) => record, "No documents have been added for this resident."),
    },
    assessments: {
      title: "Assessments",
      intro: "Assessment processes loaded for this resident.",
      rows: drawerRowsFromRecords(mappedResidentAssessments, (record) => record, "No assessment processes have been added for this resident."),
    },
    assessmentCreate: {
      title: "New Assessment",
      intro: "Add a state assessment record, attach the source files, and capture the care planning details that should feed the resident record.",
      form: "assessment",
    },
    preliminary: {
      title: "Preliminary Plans",
      intro: "Provider-created preliminary plans based on assessment, admission paperwork, and care discovery.",
      rows: drawerRowsFromRecords(mappedPreliminaryPlans, (record) => record, "No preliminary plans have been added for this resident."),
    },
    team: {
      title: "Care Teams",
      intro: "Care contacts and staff assignments loaded for this resident.",
      sections: careTeamDrawerSections.length
        ? careTeamDrawerSections
        : [{ title: "No Care Team Records", rows: [["Details", "No care team records have been added for this resident."]] }],
    },
    alerts: {
      title: "Incidents and Alerts",
      intro: "Open alert information from the resident record.",
      rows: Number(resident?.openAlertCount || 0)
        ? [["Open alerts", `${Number(resident.openAlertCount)} open alert${Number(resident.openAlertCount) === 1 ? "" : "s"}`]]
        : [["No open alerts", "No open alerts are recorded for this resident."]],
    },
  };
  const drawer = activeStaff
    ? {
        title: careTeamMemberName(activeStaff) || "Assigned staff",
        intro: `${careTeamMemberName(activeStaff) || "Care giver"} is assigned to ${residentName}.`,
        readOnly: true,
        sections: [
          {
            title: "Care Giver Details",
            rows: [
              ["Name", careTeamMemberName(activeStaff) || "Not set"],
              ["Email", careTeamEmail(activeStaff) || "Not set"],
              ["Phone", careTeamPhone(activeStaff) || "Not set"],
              ["Role", careTeamRole(activeStaff) || "Not set"],
              ["Care profile", firstText(activeStaff.careProfileId, activeStaff.profileId) || "Not set"],
              ["Care giver user", firstText(activeStaff.caregiverUserId, activeStaff.userProfileId, activeStaff.userProfile?.id, activeStaff.user?.id) || "Not set"],
            ],
          },
          {
            title: "Assignment",
            rows: [
              ["Shift", activeStaff.shift || [activeStaff.from, activeStaff.to].filter(Boolean).join(" - ") || "Not set"],
              ["Assignment notes", activeStaff.assignment || activeStaff.description || "Not set"],
            ],
          },
        ],
        contactActions: [
          ...(careTeamPhone(activeStaff) ? [["Call", "ti-phone", `tel:${String(careTeamPhone(activeStaff)).replace(/[^+\d]/g, "")}`]] : []),
          ...(careTeamEmail(activeStaff) ? [["Email", "ti-mail", `mailto:${careTeamEmail(activeStaff)}`]] : []),
        ].filter(([, , href]) => href),
      }
    : activeDrawer
      ? activeDrawer === "persona" ? residentPersonaDrawer : dataDrawerContent[activeDrawer]
      : null;
  const residentHubFacts = [
    ["Unit / Wing", residentUnit || "Not set", "ti-building-community"],
    ["Floor", residentFloor || "Not set", "ti-layers-intersect"],
    ["Room", residentRoom || "Not set", "ti-door"],
    ["Bed", residentBed || "Not set", "ti-bed"],
    ["Admission", residentAdmissionDate || "Not set", "ti-calendar-plus"],
    ["Next Review", residentNextReview || "Not set", "ti-calendar-check"],
  ];
  const residentHubStats = [
    ["Assigned Today", currentAssignedStaff.length || 0, currentAssignedStaff.length ? "Active coverage" : "Needs assignment", "ti-users-group"],
    ["Assessments", mappedResidentAssessments.length || residentAssessmentProcesses.length || 0, currentResidentAssessment ? `${recordBadge(currentResidentAssessment)} active` : "No active process", "ti-clipboard-text"],
    ["Care Plan", residentNcpStatus || "Not set", residentCareLevel || "Care level not set", "ti-heart-handshake"],
    ["Open Alerts", Number(resident?.openAlertCount || 0), Number(resident?.openAlertCount || 0) ? "Needs follow-up" : "No open alerts", "ti-alert-triangle"],
  ];
  const primaryContactDisplay = residentPrimaryContactName || "Not set";
  const assignedStaffPreview = currentAssignedStaff.slice(0, 4);

  const openDrawer = (key) => {
    if (key === "assessmentCreate") {
      if (activeAssessmentProcessReference) {
        navigate(`/assessments/${activeAssessmentProcessReference}/new`);
        return;
      }
      if (residentAssessmentType === "ANNUAL_ASSESSMENT") {
        startAnnualAssessmentProcess();
        return;
      }
      startResidentAssessment();
      return;
    }
    if (key === "residency") {
      setResidencyAssignedStaffDraft(currentAssignedStaff);
      setCaregiverSearch("");
    }
    setActiveDrawer(key);
  };
  const closeDrawer = () => setActiveDrawer(null);
  const showTab = (tabId) => activeTab === tabId;
  const selectTab = (tabId) => {
    setActiveTab(tabId);
    window.history.replaceState(null, "", `#${tabId}`);
  };

  useEffect(() => {
    const handleHashChange = () => {
      setActiveTab(window.location.hash.replace("#", "") || "overview");
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  useEffect(() => {
    if (activeDrawer !== "residency") return undefined;
    let isMounted = true;
    setCaregiverLoading(true);
    careService.careProfiles
      .list(compactObject({
        search: caregiverSearch,
        q: caregiverSearch,
        query: caregiverSearch,
        size: 20,
      }))
      .then((profiles) => {
        if (!isMounted) return;
        setCaregiverOptions(asList(profiles));
      })
      .catch((err) => {
        if (!isMounted) return;
        setCaregiverOptions([]);
        message.error(err?.response?.data?.message || err?.message || "Unable to load care givers.");
      })
      .finally(() => {
        if (isMounted) setCaregiverLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [activeDrawer, caregiverSearch]);

  useEffect(() => {
    if (!id) {
      setResidentLoading(false);
      setResidentError("Resident id is missing from the page URL.");
      return undefined;
    }
    let isMounted = true;
    setResidentLoading(true);
    loadedRelatedDataRef.current = new Set();
    loadResidentCore(id, selectedWorkspaceId)
      .then(async ([
        residentResult,
        staffResult,
        onboardingResult,
        assessmentProcessesResult,
      ]) => {
        if (!isMounted) return;
        if (residentResult.status === "rejected") {
          throw residentResult.reason;
        }
        const residentData = residentResult.value;
        const assignedStaffRows = staffResult.status === "fulfilled" ? asList(staffResult.value) : [];
        const onboardingData = onboardingResult.status === "fulfilled" ? onboardingResult.value || null : null;
        const assessmentProcesses = assessmentProcessesResult.status === "fulfilled" ? asList(assessmentProcessesResult.value) : [];
        if (!residentHasCompletedOnboarding(residentData || {}, onboardingData)) {
          const assessmentId = assessmentRedirectIdFor({
            resident: residentData || {},
            preview: onboardingData,
            processes: assessmentProcesses,
          });
          navigate(assessmentId ? `/assessments/${assessmentId}/new` : `/residents/${id}/onboarding`, { replace: true });
          return;
        }
        setResident(residentData || null);
        setAssignedStaff(assignedStaffRows);
        setResidentAssessmentProcesses(assessmentProcesses);
        setResidentRelatedRecordsError("");
        setResidentAssessmentsError("");
        if (onboardingResult.status === "rejected") {
          setOnboardingPreview(null);
          setOnboardingPreviewError(onboardingResult.reason?.response?.data?.message || onboardingResult.reason?.message || "Unable to load onboarding details.");
        } else {
          setOnboardingPreview(onboardingData);
          setOnboardingPreviewError("");
        }
        setResidentError("");
      })
      .catch((err) => {
        if (!isMounted) return;
        setResident(null);
        setAssignedStaff([]);
        setResidentAssessmentProcesses([]);
        setResidentAssessmentsError("");
        setResidentDailyNotes([]);
        setAssessmentDailyNotes([]);
        setResidentDocuments([]);
        setResidentNegotiatedCarePlans([]);
        setResidentContacts([]);
        setResidentAssessmentRecords([]);
        setResidentClockRecords([]);
        setCurrentResidentClockRecord(null);
        setResidentClockError("");
        setResidentRelatedRecordsError("");
        setOnboardingPreview(null);
        setOnboardingPreviewError("");
        setResidentError(err?.response?.data?.message || err?.message || "Unable to load resident details.");
      })
      .finally(() => {
        if (isMounted) setResidentLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [id, navigate, selectedWorkspaceId]);

  useEffect(() => {
    if (!id || residentLoading || !resident) return undefined;
    let isMounted = true;
    const loadOnce = (key, request, onSuccess, onError = setResidentRelatedRecordsError) => {
      if (loadedRelatedDataRef.current.has(key)) return;
      loadedRelatedDataRef.current.add(key);
      request()
        .then((value) => {
          if (isMounted) onSuccess(value);
        })
        .catch((error) => {
          loadedRelatedDataRef.current.delete(key);
          if (!isMounted || error?.response?.status === 404) return;
          onError(error?.response?.data?.message || error?.message || "Unable to load resident records.");
        });
    };

    const needsAssessments = activeTab === "assessment" || activeDrawer === "assessments";
    const needsDailyNotes = activeTab === "daily-notes" || activeDrawer === "notes";
    const needsDocuments = activeTab === "documents" || activeDrawer === "documents";
    const needsPlans = activeTab === "preliminary-plan" || ["preliminary", "carePlan"].includes(activeDrawer);
    const needsContacts = activeTab === "care-team" || activeDrawer === "team";
    const needsClock = activeTab === "resident-clock";

    if (needsAssessments) {
      loadOnce("assessment-records", () => careService.assessmentRecords.list({ residentId: id }), (value) => {
        setResidentAssessmentRecords(asList(value));
        setResidentAssessmentsError("");
      }, setResidentAssessmentsError);
    }
    if (needsDocuments) {
      loadOnce("documents", () => careService.documents.list({ residentId: id }), (value) => setResidentDocuments(asList(value)));
    }
    if (needsPlans) {
      loadOnce("plans", () => careService.negotiatedCarePlans.list({ residentId: id }), (value) => setResidentNegotiatedCarePlans(asList(value)));
    }
    if (needsContacts) {
      loadOnce("contacts", () => careService.residentContacts.listForResident(id), (value) => setResidentContacts(asList(value)));
    }
    if (needsDailyNotes) {
      loadOnce("daily-notes", async () => {
        const currentProcess = pickActiveAssessmentProcess(residentAssessmentProcesses);
        const processId = assessmentProcessIdValue(currentProcess) || resident.activeAssessmentProcessId || resident.currentAssessmentProcessId || resident.openAssessmentProcessId;
        const recordType = assessmentProcessRecordTypeFor(currentProcess || {}, resident);
        const requests = [careService.dailyNotes.list({ residentId: id })];
        if (processId) {
          requests.push(
            careService.assessmentRecords.byProcessAndType(processId, recordType),
            careService.dailyNotes.list(compactObject({ residentId: id, assessmentProcessId: processId, recordType, assessmentRecordType: recordType })),
          );
        }
        return Promise.allSettled(requests);
      }, (results) => {
        const residentNotes = results[0]?.status === "fulfilled" ? asList(results[0].value) : [];
        const recordNotes = results[1]?.status === "fulfilled" ? recordPayloadWithDailyNotes(results[1].value).dailyNotes : [];
        const databaseNotes = results[2]?.status === "fulfilled" ? asList(results[2].value) : [];
        setResidentDailyNotes(residentNotes);
        setAssessmentDailyNotes(mergeDailyNoteSources(databaseNotes, recordNotes));
      });
    }
    if (needsClock) {
      const params = compactObject({ workspaceId: selectedWorkspaceId, wid: selectedWorkspaceId });
      loadOnce("clock", async () => Promise.allSettled([
        careService.residentClockInOut.listForResident(id, params),
        careService.residentClockInOut.currentForResident(id, params),
      ]), ([recordsResult, currentResult]) => {
        const records = recordsResult.status === "fulfilled" ? asList(recordsResult.value) : [];
        const currentValue = currentResult.status === "fulfilled" ? currentResult.value : null;
        const current = (Array.isArray(currentValue) || Array.isArray(currentValue?.content) || Array.isArray(currentValue?.data) || Array.isArray(currentValue?.items))
          ? asList(currentValue)[0] || null
          : currentValue;
        setResidentClockRecords(records);
        setCurrentResidentClockRecord(isResidentClockedOut(current) ? current : records.find(isResidentClockedOut) || null);
        setResidentClockError("");
      }, setResidentClockError);
    }

    return () => {
      isMounted = false;
    };
  }, [activeDrawer, activeTab, id, resident, residentAssessmentProcesses, residentLoading, selectedWorkspaceId]);

  return (
    <CareShell className="dashboard-content resident-content resident-hub-page">
      <div className="crumbs">Residents <i className="ti ti-chevron-right" /> {residentName}</div>
      {residentError ? <p className="form-error">{residentError}</p> : null}
      <section className="resident-hub-hero">
        <div className="resident-hub-identity">
          <div
            className="resident-photo resident-hub-photo"
            style={{
              backgroundImage: `linear-gradient(135deg, rgba(231, 246, 239, 0.72), rgba(231, 248, 231, 0.88)), url("${publicAsset("/welcome3.png")}")`,
            }}
          >
            {residentLoading ? "..." : residentInitials}
          </div>
          <div className="resident-hub-title">
            <span>Resident Profile</span>
            <h1>{residentLoading ? "Loading resident..." : residentName}</h1>
            <div className="resident-hero-title-actions">
              <em>{residentStatus}</em>
              <em className="resident-ncp-status">NCP: {residentNcpStatus || "Not set"}</em>
              <em className="resident-care-level-status">Care: {residentCareLevel || "Not set"}</em>
            </div>
          </div>
        </div>
        <div className="resident-hub-facts">
          {residentHubFacts.map(([label, value, icon]) => (
            <div className="resident-hub-fact" key={label}>
              <i className={`ti ${icon}`} />
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
        <div className="resident-hub-status-grid" aria-label="Resident status summary">
          {residentHubStats.map(([label, value, note, icon]) => (
            <article className="resident-hub-status-card" key={label}>
              <span><i className={`ti ${icon}`} /></span>
              <div>
                <small>{label}</small>
                <strong>{value}</strong>
                <em>{note}</em>
              </div>
            </article>
          ))}
        </div>
      </section>
      <section className="resident-hub-action-center" aria-label="Resident action center">
        <button className="mini-btn action-print" type="button" onClick={() => window.print()}><i className="ti ti-printer" /> Print Summary</button>
      </section>
      <nav className="tabs">
        {residentTabs.map(([id, tab]) => (
          <button className={id === activeTab ? "active" : ""} type="button" onClick={() => selectTab(id)} key={id}>{tab}</button>
        ))}
      </nav>

      {showTab("overview") && <section className="resident-bookmark-section" id="overview">
        <div className="resident-hub-overview-grid">
          <Panel title="Resident Snapshot">
            <div className="resident-snapshot-grid">
              {snapshot.map(([label, value]) => (
                <div className="resident-snapshot-item" key={label}>
                  <span>{label}</span>
                  <strong>{value}</strong>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Care Contacts">
            <div className="resident-hub-contact-card">
              <span className="round-icon blue"><i className="ti ti-address-book" /></span>
              <div>
                <small>Primary contact</small>
                <strong>{primaryContactDisplay}</strong>
                <p>{[residentPrimaryContactRelationship, residentPrimaryContactPhone, residentPrimaryContactEmail].filter(Boolean).join(" | ") || "No contact details added"}</p>
              </div>
            </div>
          </Panel>

          <Panel title="Today's Coverage">
            {assignedStaffPreview.length ? (
              <div className="resident-hub-team-list">
                {assignedStaffPreview.map((staff) => (
                  <button className="resident-hub-team-row" type="button" onClick={() => openDrawer(staffDrawerId(staff))} key={staffDrawerId(staff) || careTeamMemberName(staff)}>
                    <span>{initialsFor(careTeamMemberName(staff) || "Staff")}</span>
                    <div>
                      <strong>{careTeamMemberName(staff) || "Assigned staff"}</strong>
                      <small>{careTeamEmail(staff) || careTeamRole(staff) || staff.shift || "Care team"}</small>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="resident-inline-empty">No staff assignment found for today.</div>
            )}
          </Panel>
        </div>

        <Panel title="Resident Information and Actions">
          <div className="resident-section-grid">
            {residentActionSections.map(([key, icon, title, text, status, createdAt]) => (
              <button className="resident-section-card" type="button" onClick={() => openDrawer(key)} key={title}>
                <span className="round-icon blue"><i className={`ti ${icon}`} /></span>
                <div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                  <small>Updated {formatDate(createdAt)}</small>
                </div>
                <em>{status}</em>
              </button>
            ))}
          </div>
        </Panel>
      </section>}

      {showTab("onboarding-bio") && <section className="resident-bookmark-section" id="onboarding-bio">
        <OnboardingDataTab
          title="Onboarding Bio Data"
          intro="Personal bio data, address, insurance, and coverage details captured during resident onboarding."
          sections={onboardingBioSections}
          error={onboardingPreviewError}
          onEdit={() => openDrawer("persona")}
        />
      </section>}

      {showTab("residency-details") && <section className="resident-bookmark-section" id="residency-details">
        <OnboardingDataTab
          title="Residency Details"
          intro="Residence placement, floor, room, bed, and assigned staff for this resident."
          sections={residencyDetailSections}
          error={residentRelatedRecordsError}
          onAction={(action) => action?.staffId && openDrawer(action.staffId)}
          onEdit={() => openDrawer("residency")}
        />
      </section>}

      {showTab("onboarding-contacts") && <section className="resident-bookmark-section" id="onboarding-contacts">
        <OnboardingDataTab
          title="Onboarding Contacts"
          intro="Family, guardians, PoA, emergency contacts, and care-circle contacts captured during onboarding."
          sections={onboardingContactSections}
          error={onboardingPreviewError}
          onEdit={() => openDrawer("contacts")}
        />
      </section>}

      {showTab("onboarding-care-history") && <section className="resident-bookmark-section" id="onboarding-care-history">
        <OnboardingDataTab
          title="Onboarding Care History"
          intro="Hospital admission, prior family home, prior caregiver, and care history details captured during onboarding."
          sections={onboardingCareHistorySections}
          error={onboardingPreviewError}
          onEdit={() => openDrawer("careHistory")}
        />
      </section>}

      {showTab("onboarding-care-snapshot") && <section className="resident-bookmark-section" id="onboarding-care-snapshot">
        <OnboardingDataTab
          title="Onboarding Care Snapshot"
          intro="Initial condition, diagnosis, allergies, medications, care needs, behavior, and custom care questions."
          sections={onboardingCareSnapshotSections}
          error={onboardingPreviewError}
          onEdit={() => openDrawer("careSnapshot")}
        />
      </section>}

      {showTab("onboarding-documents") && <section className="resident-bookmark-section" id="onboarding-documents">
        <OnboardingDataTab
          title="Onboarding Documents"
          intro="Uploaded files and signed-document validation captured during onboarding."
          sections={onboardingDocumentSections}
          error={onboardingPreviewError}
          onEdit={() => openDrawer("onboardingDocuments")}
        />
      </section>}

      {showTab("assessment") && <section className="resident-bookmark-section" id="assessment">
        <ResidentTabList
          title="Assessments"
          intro="State assessments, MaG evaluations, annual reviews, and final assessment updates for this resident."
          tab="assessment"
          onCreate={openDrawer}
          currentOverride={currentResidentAssessment}
          rowsOverride={previousResidentAssessments}
          loading={residentLoading}
          error={residentAssessmentsError}
          showCreate={showStartNewAssessment}
          onEdit={() => openDrawer("assessments")}
        />
      </section>}

      {showTab("preliminary-plan") && <section className="resident-bookmark-section" id="preliminary-plan">
        <ResidentTabList
          title="Preliminary Plans"
          intro="Provider-created preliminary plans based on admission paperwork, state assessment, MaG, and family discovery."
          tab="preliminary-plan"
          onCreate={openDrawer}
          currentOverride={currentPreliminaryPlan}
          rowsOverride={previousPreliminaryPlans}
          loading={residentLoading}
          error={residentRelatedRecordsError}
          onEdit={() => openDrawer("preliminary")}
        />
      </section>}

      {showTab("daily-notes") && <section className="resident-bookmark-section" id="daily-notes">
        <ResidentTabList
          title="Daily Notes"
          intro="Daily notes capture behavior, health, welfare, discrepancies, and care changes as they happen."
          tab="daily-notes"
          onCreate={openDrawer}
          currentOverride={currentDailyNote}
          rowsOverride={previousDailyNotes}
          loading={residentLoading}
          error={residentRelatedRecordsError}
          showCreate={false}
        />
      </section>}

      {showTab("documents") && <section className="resident-bookmark-section" id="documents">
        <ResidentTabList
          title="Documents"
          intro="Signed agreements, policies, PoA documentation, state assessment files, and confirmed care plan files."
          tab="documents"
          onCreate={openDrawer}
          currentOverride={currentDocument}
          rowsOverride={previousDocuments}
          loading={residentLoading}
          error={residentRelatedRecordsError}
          onEdit={() => openDrawer("documents")}
        />
      </section>}

      {showTab("care-team") && <section className="resident-bookmark-section" id="care-team">
        <ResidentTabList
          title="Care Teams"
          intro="Coordinate updates with PCP, physical therapy, occupational therapy, family or PoA, social worker, and internal staff."
          tab="care-team"
          onCreate={openDrawer}
          currentOverride={currentCareTeamMember}
          rowsOverride={previousCareTeamMembers}
          loading={residentLoading}
          error={residentRelatedRecordsError}
          onEdit={() => openDrawer("team")}
        />
      </section>}

      {showTab("resident-clock") && <section className="resident-bookmark-section" id="resident-clock">
        <ResidentClockInOutTab
          residentId={id}
          residentName={residentName}
          records={residentClockRecords}
          currentRecord={currentResidentClockRecord}
          loading={residentLoading}
          error={residentClockError}
          staffOptions={currentAssignedStaff}
          actorId={capturedByUserId}
          actorName={capturedByName}
          workspaceId={selectedWorkspaceId}
          onRecordsChange={setResidentClockRecords}
          onCurrentRecordChange={setCurrentResidentClockRecord}
        />
      </section>}
      <Drawer
        title={drawer?.title}
        placement="right"
        open={Boolean(drawer)}
        onClose={closeDrawer}
        width={520}
        className="resident-action-drawer"
      >
        {drawer ? (
          <div className="resident-drawer-content">
            <p>{drawer.intro}</p>
            {drawer.form === "assessment" ? (
              <AssessmentDrawerForm />
            ) : drawer.readOnly ? (
              <DrawerDetailSections sections={drawer.sections} />
            ) : drawer.sections ? (
              <DrawerEditSections
                onCancel={closeDrawer}
                onSave={drawer.onSave}
                saving={drawer.saving}
                sections={drawer.sections}
                staffPicker={drawer.staffPicker}
              />
            ) : (
              <DrawerEditSections onCancel={closeDrawer} sections={[{ title: drawer.title || "Details", rows: drawer.rows || [] }]} />
            )}
            {drawer.contactActions ? (
              <div className="resident-contact-actions">
                {drawer.contactActions.map(([label, icon, href]) => (
                  <a className="mini-btn" href={href} key={label}><i className={`ti ${icon}`} /> {label}</a>
                ))}
              </div>
            ) : null}
            {!drawer.form && drawer.action ? (
              <button className="btn primary" type="button">{drawer.action}</button>
            ) : null}
          </div>
        ) : null}
      </Drawer>
    </CareShell>
  );
}
